
import React, { useEffect, useRef, useState, useCallback, useMemo, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';
import { ZoomIn, ZoomOut, Camera, Mic, Loader2, AlertCircle, Hand, MousePointer2, Type, Trash2, Layers, X, MoveHorizontal, Plus, Highlighter, PenTool, Eraser, Maximize, Palette, ChevronUp, ChevronDown, Sparkles, List, ChevronRight, Square, ArrowUpRight, BookOpen, Lightbulb, Moon, Sun, Search, Copy, Check } from 'lucide-react';
import { PageVocabularyPanel } from './PageVocabularyPanel';
import { MobileVoiceCaptureOverlay } from './MobileVoiceCaptureOverlay';
import { Stroke, Language } from '../types';


export interface PdfDocumentUtils {
  getPageText: (pageNum: number) => Promise<string>;
  getPageThumbnail: (pageNum: number) => Promise<string>;
  captureCurrentPage: (pageNum: number) => Promise<string>;
  captureSegment: (pageNum: number, startText: string, endText: string, keyHighlights: string[], colorIndex: number) => Promise<string>;
}

interface PdfViewerProps {
  url: string;
  onSnap: (base64: string, extractedText?: string) => void;
  onPageChange?: (page: number) => void;
  annotations?: { [pageNumber: number]: Stroke[] };
  onAnnotationChange?: (pageNumber: number, strokes: Stroke[]) => void;
  onDocumentReady?: (utils: PdfDocumentUtils) => void;
  onGuidedReading?: (pageText: string, pageNumber: number) => void;
  guidedReadingHighlights?: { text: string; colorIndex: number }[];
  isGuidedReadingLoading?: boolean;
  language?: Language;
  userEmail?: string;
  documentTitle?: string;
  boardTitle?: string;
  highlightCaptureButton?: boolean;
  isMobile?: boolean;
  onVoiceCapture?: (imageBase64: string, transcript: string) => void;
  selectedNoteName?: string;
  toolbarPortalTarget?: HTMLDivElement | null;
}

type ToolType = 'move' | 'select' | 'pen' | 'highlight' | 'eraser' | 'crop' | 'text_highlight' | 'rect' | 'arrow';

interface OutlineItem {
  title: string;
  pageNumber: number;
  level: number;
  key: string;
  children: OutlineItem[];
}

// Default Palettes
const PEN_COLORS = [
    '#000000', '#2563eb', '#dc2626', '#16a34a', '#d97706', '#9333ea',
    '#4b5563', '#0ea5e9'
];

const HIGHLIGHT_COLORS = [
    '#fde047', '#86efac', '#93c5fd', '#f9a8d4', '#d8b4fe', '#fdba74',
    '#fef08a', '#bbf7d0'
];

const FULL_COLOR_PALETTE = [
    '#fef08a','#fde047','#fbbf24','#f97316','#ef4444','#dc2626',
    '#f9a8d4','#f472b6','#e879f9','#c084fc','#a855f7','#9333ea',
    '#93c5fd','#60a5fa','#38bdf8','#22d3ee','#2dd4bf','#34d399',
    '#86efac','#4ade80','#a3e635','#d8b4fe','#fdba74','#fcd34d',
    '#000000','#374151','#6b7280','#9ca3af','#d1d5db','#ffffff',
];

const ColorPickerGrid = ({ onSelect, onClose }: { onSelect: (c: string) => void; onClose: () => void }) => (
    <div className="grid grid-cols-6 gap-1.5 pt-1" onPointerDown={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()}>
        {FULL_COLOR_PALETTE.map(c => (
            <button key={c} className="w-6 h-6 rounded-full border border-gray-200 dark:border-slate-600 hover:scale-125 transition-transform flex-shrink-0"
                style={{ backgroundColor: c }}
                onPointerDown={e => { e.stopPropagation(); e.preventDefault(); onSelect(c); onClose(); }} />
        ))}
    </div>
);

// --- SUB-COMPONENT: PDF PAGE WITH ANNOTATION LAYER ---
const PdfPage = React.memo(({
  pdfDoc,
  pageNumber,
  scale,
  setPageRef,
  setHighlightLayerRef,
  getHighlightLayerCanvas,
  scrollContainerRef,
  activeTool,
  registerAnnotationRef,
  onSnap,
  onCropComplete,
  initialStrokes,
  onStrokesChange,
  currentPenWidth,
  currentHighlightWidth,
  currentColor,
  customColors,
  onSaveHighlightColor,
  language,
  guidedReadingHighlights = [],
  setTextLayerRef,
}: {
  pdfDoc: any,
  pageNumber: number,
  scale: number,
  setPageRef: (pageNum: number, canvas: HTMLCanvasElement | null) => void,
  setHighlightLayerRef?: (pageNum: number, canvas: HTMLCanvasElement | null) => void,
  getHighlightLayerCanvas?: (pageNum: number) => HTMLCanvasElement | null,
  scrollContainerRef: React.RefObject<HTMLDivElement>,
  activeTool: ToolType,
  registerAnnotationRef: (pageNum: number, getStrokes: () => Stroke[]) => void,
  onSnap: (base64: string, extractedText?: string) => void,
  onCropComplete: () => void,
  initialStrokes?: Stroke[],
  onStrokesChange?: (strokes: Stroke[]) => void,
  currentPenWidth: number,
  currentHighlightWidth: number,
  currentColor: string,
  customColors: string[],
  onSaveHighlightColor: (c: string) => void,
  language: string,
  guidedReadingHighlights?: { text: string; colorIndex: number }[],
  setTextLayerRef?: (pageNum: number, el: HTMLDivElement | null) => void,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Two canvas layers: one for multiplying highlights, one for normal drawing
  const highlightLayerRef = useRef<HTMLCanvasElement>(null);
  const annotationRef = useRef<HTMLCanvasElement>(null);

  const textLayerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const pageCustomColorInputRef = useRef<HTMLInputElement>(null);
  const isPanning = useRef(false);
  const panStart = useRef({ mouseX: 0, mouseY: 0, scrollTop: 0, scrollLeft: 0 });
  const [showPickerInStroke, setShowPickerInStroke] = useState(false);

  const [isRendered, setIsRendered] = useState(false);

  // Register textLayerRef with PdfViewer so captureSegment can access it
  // Must be declared AFTER isRendered to avoid TDZ
  useEffect(() => {
    if (!isRendered) return;
    setTextLayerRef?.(pageNumber, textLayerRef.current);
    return () => { setTextLayerRef?.(pageNumber, null); };
  }, [pageNumber, setTextLayerRef, isRendered]);
  const [pageDimensions, setPageDimensions] = useState<{width: number, height: number} | null>(null);
  const renderTaskRef = useRef<any>(null);
  
  const strokesRef = useRef<Stroke[]>(initialStrokes || []);
  const isDrawing = useRef(false);
  const currentPath = useRef<{x: number, y: number}[]>([]);
  const previewStrokeRef = useRef<Stroke | null>(null);

  // Selection State
  const [selectedStrokeIndex, setSelectedStrokeIndex] = useState<number | null>(null);
  const [copiedHighlight, setCopiedHighlight] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{x: number, y: number} | null>(null);
  
  // Hover State for intelligent tool switching
  const [hoveredStrokeIndex, setHoveredStrokeIndex] = useState<number | null>(null);

  useEffect(() => {
      if (initialStrokes) {
          strokesRef.current = initialStrokes;
          if (isRendered) drawAnnotations();
      }
  }, [initialStrokes, isRendered]);

  const [cropSelection, setCropSelection] = useState<{x: number, y: number, w: number, h: number} | null>(null);
  const cropStartRef = useRef<{x: number, y: number} | null>(null);
  const [cropMousePos, setCropMousePos] = useState<{x: number, y: number} | null>(null);

  useEffect(() => {
      registerAnnotationRef(pageNumber, () => strokesRef.current);
  }, [pageNumber, registerAnnotationRef]);

  // Clear selection if tool changes
  useEffect(() => {
      if (activeTool === 'move' || activeTool === 'crop' || activeTool === 'eraser') {
          setSelectedStrokeIndex(null);
          setMenuPosition(null);
          setHoveredStrokeIndex(null);
          drawAnnotations(); 
      }
  }, [activeTool]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          renderPage();
        }
      },
      { 
          root: scrollContainerRef.current,
          rootMargin: '100% 0px 100% 0px', 
          threshold: 0 
      } 
    );

    observer.observe(container);
    return () => observer.disconnect();
  }, [pageNumber, scale, pdfDoc]);

  useEffect(() => {
      if (isRendered) {
          drawAnnotations();
          if (textLayerRef.current) {
               textLayerRef.current.style.setProperty('--scale-factor', `${scale}`);
          }
      }
  }, [scale, isRendered]);

  // Redraw when selection or hover changes to update visuals
  useEffect(() => {
      if (isRendered) drawAnnotations();
  }, [selectedStrokeIndex, hoveredStrokeIndex]);

  const drawAnnotations = () => {
      const fgCanvas = annotationRef.current;
      const hlCanvas = highlightLayerRef.current;
      if (!fgCanvas || !hlCanvas || !pageDimensions) return;
      
      const fgCtx = fgCanvas.getContext('2d');
      const hlCtx = hlCanvas.getContext('2d');
      if (!fgCtx || !hlCtx) return;

      const w = pageDimensions.width * scale;
      const h = pageDimensions.height * scale;
      const pixelRatio = window.devicePixelRatio || 1;

      // setup FG
      fgCanvas.width = w * pixelRatio;
      fgCanvas.height = h * pixelRatio;
      fgCtx.scale(pixelRatio, pixelRatio);
      fgCtx.clearRect(0, 0, w, h);
      fgCtx.lineCap = 'round';
      fgCtx.lineJoin = 'round';

      // setup HL
      hlCanvas.width = w * pixelRatio;
      hlCanvas.height = h * pixelRatio;
      hlCtx.scale(pixelRatio, pixelRatio);
      hlCtx.clearRect(0, 0, w, h);
      hlCtx.lineCap = 'round';
      hlCtx.lineJoin = 'round';

      strokesRef.current.forEach((stroke, index) => {
          const isHighlight = stroke.type === 'highlight' || stroke.isTextHighlight;
          const ctx = isHighlight ? hlCtx : fgCtx;

          ctx.beginPath();
          ctx.strokeStyle = stroke.color;
          ctx.globalAlpha = isHighlight ? Math.min(stroke.alpha + 0.2, 1.0) : stroke.alpha;
          ctx.globalCompositeOperation = 'source-over';

          if (stroke.isTextHighlight && stroke.rects) {
              ctx.fillStyle = stroke.color;
              stroke.rects.forEach(r => {
                  ctx.fillRect(r.x * w, r.y * h, r.w * w, r.h * h);
              });
          } else if (stroke.type === 'rect' && stroke.points.length >= 2) {
              const [p1, p2] = stroke.points;
              ctx.lineWidth = stroke.width * scale;
              ctx.strokeRect(p1.x * w, p1.y * h, (p2.x - p1.x) * w, (p2.y - p1.y) * h);
          } else if (stroke.type === 'arrow' && stroke.points.length >= 2) {
              const [p1, p2] = stroke.points;
              const x1 = p1.x * w, y1 = p1.y * h;
              const x2 = p2.x * w, y2 = p2.y * h;
              ctx.lineWidth = stroke.width * scale;
              ctx.beginPath();
              ctx.moveTo(x1, y1);
              ctx.lineTo(x2, y2);
              ctx.stroke();
              const angle = Math.atan2(y2 - y1, x2 - x1);
              const headLen = Math.max(stroke.width * scale * 4, 14);
              ctx.beginPath();
              ctx.moveTo(x2, y2);
              ctx.lineTo(x2 - headLen * Math.cos(angle - Math.PI / 6), y2 - headLen * Math.sin(angle - Math.PI / 6));
              ctx.moveTo(x2, y2);
              ctx.lineTo(x2 - headLen * Math.cos(angle + Math.PI / 6), y2 - headLen * Math.sin(angle + Math.PI / 6));
              ctx.stroke();
          } else if (stroke.points.length > 1) {
              ctx.lineWidth = stroke.width * scale;
              const start = stroke.points[0];
              ctx.moveTo(start.x * w, start.y * h);

              for (let i = 1; i < stroke.points.length; i++) {
                  const p = stroke.points[i];
                  ctx.lineTo(p.x * w, p.y * h);
              }
              ctx.stroke();
          }

          // Draw Selection Box
          if (index === selectedStrokeIndex || index === hoveredStrokeIndex) {
              const isHover = index === hoveredStrokeIndex && index !== selectedStrokeIndex;
              fgCtx.globalAlpha = 1.0;
              fgCtx.globalCompositeOperation = 'source-over';
              fgCtx.strokeStyle = isHover ? '#60a5fa' : '#3b82f6';
              fgCtx.lineWidth = isHover ? 1 : 2;

              if (isHover) {
                  fgCtx.setLineDash([2, 2]);
              } else {
                  fgCtx.setLineDash([5, 5]);
              }

              if (stroke.isTextHighlight && stroke.rects) {
                  stroke.rects.forEach(r => {
                      fgCtx.strokeRect(r.x * w - 2, r.y * h - 2, r.w * w + 4, r.h * h + 4);
                  });
              } else if (stroke.points.length > 0) {
                  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
                  stroke.points.forEach(p => {
                      minX = Math.min(minX, p.x);
                      minY = Math.min(minY, p.y);
                      maxX = Math.max(maxX, p.x);
                      maxY = Math.max(maxY, p.y);
                  });
                  fgCtx.strokeRect(minX * w - 5, minY * h - 5, (maxX - minX) * w + 10, (maxY - minY) * h + 10);
              }
              fgCtx.setLineDash([]);
          }
      });

      // Draw live preview for rect/arrow while dragging
      const ps = previewStrokeRef.current;
      if (ps && ps.points.length >= 2) {
          fgCtx.strokeStyle = ps.color;
          fgCtx.globalAlpha = ps.alpha;
          fgCtx.globalCompositeOperation = 'source-over';
          fgCtx.lineWidth = ps.width * scale;
          fgCtx.lineCap = 'round';
          fgCtx.lineJoin = 'round';
          const [p1, p2] = ps.points;
          if (ps.type === 'rect') {
              fgCtx.strokeRect(p1.x * w, p1.y * h, (p2.x - p1.x) * w, (p2.y - p1.y) * h);
          } else if (ps.type === 'arrow') {
              const x1 = p1.x * w, y1 = p1.y * h;
              const x2 = p2.x * w, y2 = p2.y * h;
              fgCtx.beginPath();
              fgCtx.moveTo(x1, y1);
              fgCtx.lineTo(x2, y2);
              fgCtx.stroke();
              const angle = Math.atan2(y2 - y1, x2 - x1);
              const headLen = Math.max(ps.width * scale * 4, 14);
              fgCtx.beginPath();
              fgCtx.moveTo(x2, y2);
              fgCtx.lineTo(x2 - headLen * Math.cos(angle - Math.PI / 6), y2 - headLen * Math.sin(angle - Math.PI / 6));
              fgCtx.moveTo(x2, y2);
              fgCtx.lineTo(x2 - headLen * Math.cos(angle + Math.PI / 6), y2 - headLen * Math.sin(angle + Math.PI / 6));
              fgCtx.stroke();
          }
      }
  };

  const renderPage = async () => {
    if (!pdfDoc || !canvasRef.current) return;
    
    const requiredWidth = Math.floor(pageDimensions ? pageDimensions.width * scale * (window.devicePixelRatio || 1) : 0);
    const canvasCurrentWidth = canvasRef.current.width;
    const isResizing = pageDimensions && Math.abs(canvasCurrentWidth - requiredWidth) > 10;
    
    if (isRendered && !isResizing && pageDimensions) {
         drawAnnotations();
         return; 
    }

    try {
      const page = await pdfDoc.getPage(pageNumber);
      const outputScale = window.devicePixelRatio || 1;
      const viewport = page.getViewport({ scale });
      
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      
      canvas.width = Math.floor(viewport.width * outputScale);
      canvas.height = Math.floor(viewport.height * outputScale);
      
      setPageDimensions({ width: viewport.width / scale, height: viewport.height / scale });

      const transform = outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : null;

      if (renderTaskRef.current) {
          try { renderTaskRef.current.cancel(); } catch(e) {}
      }

      renderTaskRef.current = page.render({
        canvasContext: context,
        transform: transform,
        viewport: viewport,
      });

      await renderTaskRef.current.promise;
      
      if (textLayerRef.current) {
          const textContent = await page.getTextContent();
          textLayerRef.current.innerHTML = '';
          textLayerRef.current.style.setProperty('--scale-factor', `${scale}`);
          
          // @ts-ignore
          if (window.pdfjsLib) {
             // @ts-ignore
             window.pdfjsLib.renderTextLayer({
                textContentSource: textContent,
                container: textLayerRef.current,
                viewport: viewport,
                textDivs: []
             });
          }
      }

      setIsRendered(true);
      setPageRef(pageNumber, canvas);
      setHighlightLayerRef?.(pageNumber, highlightLayerRef.current);
      requestAnimationFrame(drawAnnotations);

    } catch (err: any) {
      if (err.name !== 'RenderingCancelledException') {
         // console.error("Render error", err);
      }
    }
  };

  useEffect(() => {
    return () => {
      if (renderTaskRef.current) renderTaskRef.current.cancel();
    };
  }, []);

  // Apply AI guided reading highlights whenever highlights or render state changes
  useEffect(() => {
    if (!textLayerRef.current || !isRendered) return;
    // Remove all existing AI highlight classes
    textLayerRef.current.querySelectorAll('span[class*="ai-hl-"]').forEach(el => {
        Array.from(el.classList).filter(c => c.startsWith('ai-hl-')).forEach(c => el.classList.remove(c));
    });
    if (guidedReadingHighlights.length === 0) return;

    const spans = Array.from(textLayerRef.current.querySelectorAll('span'));
    if (spans.length === 0) return;

    // Build a position map: concatenate all span texts to find multi-span phrases
    const spanData: { span: Element; start: number; end: number }[] = [];
    let pos = 0;
    for (const span of spans) {
        const len = (span.textContent || '').length;
        spanData.push({ span, start: pos, end: pos + len });
        pos += len + 1;
    }
    const fullText = spanData.map(d => d.span.textContent || '').join(' ').toLowerCase();

    guidedReadingHighlights.forEach(({ text: phrase, colorIndex }) => {
        const lower = phrase.toLowerCase().trim();
        if (!lower) return;

        let matchIdx = fullText.indexOf(lower);
        if (matchIdx === -1) {
            const firstLongWord = lower.split(/\s+/).find(w => w.length >= 5);
            if (firstLongWord) matchIdx = fullText.indexOf(firstLongWord);
        }
        if (matchIdx === -1) return;
        const matchEnd = matchIdx + lower.length;

        const cls = `ai-hl-${colorIndex % 4}`;
        for (const { span, start, end } of spanData) {
            if (end > matchIdx && start < matchEnd) {
                span.classList.add(cls);
            }
        }
    });
  }, [guidedReadingHighlights, isRendered]);

  const getPoint = (e: React.PointerEvent) => {
      const canvas = annotationRef.current;
      if (!canvas) return { x: 0, y: 0 };
      const rect = canvas.getBoundingClientRect();
      return {
          x: (e.clientX - rect.left),
          y: (e.clientY - rect.top)
      };
  };

  const hitTestStroke = (x: number, y: number, w: number, h: number): number => {
      const nx = x / w;
      const ny = y / h;

      const distToSegment = (px: number, py: number, ax: number, ay: number, bx: number, by: number) => {
          const dx = bx - ax, dy = by - ay;
          const lenSq = dx * dx + dy * dy;
          if (lenSq === 0) return Math.hypot(px - ax, py - ay);
          const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / lenSq));
          return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
      };

      for (let i = strokesRef.current.length - 1; i >= 0; i--) {
          const stroke = strokesRef.current[i];
          const threshold = (stroke.width * scale / 2) + 6;
          if (stroke.isTextHighlight && stroke.rects) {
              const hit = stroke.rects.some(r => nx >= r.x && nx <= r.x + r.w && ny >= r.y && ny <= r.y + r.h);
              if (hit) return i;
          } else if (stroke.type === 'rect' && stroke.points.length >= 2) {
              // Hit if near any of the 4 edges
              const [p1, p2] = stroke.points;
              const rx1 = p1.x * w, ry1 = p1.y * h, rx2 = p2.x * w, ry2 = p2.y * h;
              const edges = [
                  [rx1, ry1, rx2, ry1], [rx2, ry1, rx2, ry2],
                  [rx2, ry2, rx1, ry2], [rx1, ry2, rx1, ry1],
              ];
              if (edges.some(([ax, ay, bx, by]) => distToSegment(x, y, ax, ay, bx, by) <= threshold)) return i;
          } else if (stroke.type === 'arrow' && stroke.points.length >= 2) {
              const [p1, p2] = stroke.points;
              if (distToSegment(x, y, p1.x * w, p1.y * h, p2.x * w, p2.y * h) <= threshold) return i;
          } else if (stroke.points) {
              const hit = stroke.points.some(p => Math.hypot(p.x * w - x, p.y * h - y) <= threshold);
              if (hit) return i;
          }
      }
      return -1;
  };

  const handleContainerPointerDown = (e: React.PointerEvent) => {
      if (activeTool !== 'move') return;
      isPanning.current = true;
      panStart.current = {
          mouseX: e.clientX,
          mouseY: e.clientY,
          scrollTop: scrollContainerRef.current?.scrollTop ?? 0,
          scrollLeft: scrollContainerRef.current?.scrollLeft ?? 0,
      };
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      e.preventDefault();
  };

  const handleContainerPointerUp = (e: React.PointerEvent) => {
      if (!isPanning.current) return;
      isPanning.current = false;
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
  };

  const handleContainerPointerMove = (e: React.PointerEvent) => {
      if (isPanning.current && activeTool === 'move' && scrollContainerRef.current) {
          const dx = e.clientX - panStart.current.mouseX;
          const dy = e.clientY - panStart.current.mouseY;
          scrollContainerRef.current.scrollTop = panStart.current.scrollTop - dy;
          scrollContainerRef.current.scrollLeft = panStart.current.scrollLeft - dx;
          return;
      }

      const rect = containerRef.current?.getBoundingClientRect();
      const canvas = annotationRef.current;
      if (!rect || !canvas) return;

      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      // Track mouse for crosshair overlay in crop mode
      if (activeTool === 'crop') {
          setCropMousePos({ x, y });
      }

      if (isDrawing.current) return;

      const w = canvas.width / (window.devicePixelRatio || 1);
      const h = canvas.height / (window.devicePixelRatio || 1);

      const hitIdx = hitTestStroke(x, y, w, h);
      const newHoverIdx = hitIdx === -1 ? null : hitIdx;

      if (newHoverIdx !== hoveredStrokeIndex) {
          setHoveredStrokeIndex(newHoverIdx);
      }
  };

  const handleContainerClick = (e: React.MouseEvent) => {
    if (activeTool !== 'text_highlight' && activeTool !== 'move') return;

    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const canvas = annotationRef.current;
    if (!canvas) return;
    const w = canvas.width / (window.devicePixelRatio || 1);
    const h = canvas.height / (window.devicePixelRatio || 1);

    const hitIdx = hitTestStroke(x, y, w, h);
    if (hitIdx !== -1) {
         setSelectedStrokeIndex(hitIdx);
         setMenuPosition({ x, y });
    } else {
         const sel = window.getSelection();
         if (!sel || sel.isCollapsed) {
             setSelectedStrokeIndex(null);
             setMenuPosition(null);
         }
    }
  };

  const handlePointerDown = (e: React.PointerEvent) => {
      // Allow pinch/pan by ignoring pointer down in move mode
      if (activeTool === 'move') return;
      
      const canvas = annotationRef.current;
      if (!canvas) return;

      const { x, y } = getPoint(e);
      const w = canvas.width / (window.devicePixelRatio || 1);
      const h = canvas.height / (window.devicePixelRatio || 1);

      const hitIdx = hitTestStroke(x, y, w, h);
      
      if (hitIdx !== -1 && ['select', 'highlight', 'pen', 'text_highlight', 'rect', 'arrow'].includes(activeTool)) {
          e.stopPropagation();
          e.preventDefault();
          setSelectedStrokeIndex(hitIdx);
          setMenuPosition({ x, y });
          return; 
      }

      if (activeTool === 'select') {
          setSelectedStrokeIndex(null);
          setMenuPosition(null);
          return;
      }
      
      if (activeTool === 'text_highlight') {
          return; // Let it bubble to text layer
      }

      e.stopPropagation();
      e.preventDefault(); 
      
      canvas.setPointerCapture(e.pointerId);
      isDrawing.current = true;

      if (activeTool === 'crop') {
          cropStartRef.current = { x, y };
          setCropSelection({ x, y, w: 0, h: 0 });
          return;
      }
      
      setSelectedStrokeIndex(null);
      setMenuPosition(null);

      currentPath.current = [{ x: x/w, y: y/h }];

      if (activeTool === 'eraser') {
          processEraser(x, y, w, h);
      }
  };

  const handlePointerMove = (e: React.PointerEvent) => {
      if (!isDrawing.current || activeTool === 'move' || activeTool === 'text_highlight' || activeTool === 'select') return;
      
      e.preventDefault(); 
      e.stopPropagation();

      const { x, y } = getPoint(e);

      if (activeTool === 'crop') {
          setCropMousePos({ x, y });
          if (!cropStartRef.current) return;
          const sx = cropStartRef.current.x;
          const sy = cropStartRef.current.y;
          setCropSelection({
              x: Math.min(sx, x),
              y: Math.min(sy, y),
              w: Math.abs(x - sx),
              h: Math.abs(y - sy)
          });
          return;
      }

      const isHighlightTool = activeTool === 'highlight';
      const canvas = isHighlightTool ? highlightLayerRef.current : annotationRef.current;
      const ctx = canvas?.getContext('2d');
      if (!canvas || !ctx) return;

      const w = canvas.width / (window.devicePixelRatio || 1);
      const h = canvas.height / (window.devicePixelRatio || 1);

      if (activeTool === 'eraser') {
          processEraser(x, y, w, h);
          return;
      }

      if (activeTool === 'rect' || activeTool === 'arrow') {
          const start = currentPath.current[0];
          if (start) {
              previewStrokeRef.current = {
                  points: [start, { x: x / w, y: y / h }],
                  color: currentColor,
                  width: currentPenWidth,
                  alpha: 1.0,
                  type: activeTool,
              };
              drawAnnotations();
          }
          return;
      }

      currentPath.current.push({ x: x/w, y: y/h });

      const pts = currentPath.current;
      if (pts.length >= 2) {
          const p1 = pts[pts.length - 2];
          const p2 = pts[pts.length - 1];

          ctx.beginPath();
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';
          
          if (activeTool === 'highlight') {
              ctx.strokeStyle = currentColor; 
              ctx.lineWidth = currentHighlightWidth * scale;
              ctx.globalAlpha = 0.6;
              ctx.globalCompositeOperation = 'source-over';
          } else {
              ctx.strokeStyle = currentColor; 
              ctx.lineWidth = currentPenWidth * scale;
              ctx.globalAlpha = 1.0;
              ctx.globalCompositeOperation = 'source-over';
          }
          
          ctx.moveTo(p1.x * w, p1.y * h);
          ctx.lineTo(p2.x * w, p2.y * h);
          ctx.stroke();
      }
  };

  const handlePointerUp = (e: React.PointerEvent) => {
      if (!isDrawing.current || activeTool === 'move' || activeTool === 'text_highlight' || activeTool === 'select') return;
      isDrawing.current = false;
      
      const canvas = annotationRef.current;
      if(canvas) canvas.releasePointerCapture(e.pointerId);

      if (activeTool === 'crop') {
          if (cropSelection && cropSelection.w > 10 && cropSelection.h > 10) {
              performCrop();
          }
          setCropSelection(null);
          cropStartRef.current = null;
          onCropComplete();
          return;
      }

      if (activeTool === 'rect' || activeTool === 'arrow') {
          previewStrokeRef.current = null;
          const canvas = annotationRef.current;
          if (canvas && currentPath.current.length > 0) {
              const cw = canvas.width / (window.devicePixelRatio || 1);
              const ch = canvas.height / (window.devicePixelRatio || 1);
              const { x, y } = getPoint(e);
              const start = currentPath.current[0];
              const end = { x: x / cw, y: y / ch };
              const dx = Math.abs((end.x - start.x) * cw);
              const dy = Math.abs((end.y - start.y) * ch);
              if (dx > 4 || dy > 4) {
                  const newStroke: Stroke = {
                      points: [start, end],
                      color: currentColor,
                      width: currentPenWidth,
                      alpha: 1.0,
                      type: activeTool,
                  };
                  strokesRef.current.push(newStroke);
                  if (onStrokesChange) onStrokesChange([...strokesRef.current]);
              }
          }
          drawAnnotations();
          currentPath.current = [];
          return;
      }

      if (activeTool !== 'eraser' && currentPath.current.length > 1) {
          const newStroke: Stroke = {
              points: [...currentPath.current],
              color: currentColor,
              width: activeTool === 'highlight' ? currentHighlightWidth : currentPenWidth,
              alpha: activeTool === 'highlight' ? 0.4 : 1.0,
              type: activeTool === 'highlight' ? 'highlight' : 'pen'
          };
          strokesRef.current.push(newStroke);

          drawAnnotations();

          if (onStrokesChange) {
              onStrokesChange([...strokesRef.current]);
          }
      }
      currentPath.current = [];
  };

  const attemptTextHighlight = useCallback((colorOverride?: string, alphaOverride?: number) => {
      // In text_highlight mode, this function is triggered manually via event or button
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return;

      const range = selection.getRangeAt(0);
      const containerRect = containerRef.current?.getBoundingClientRect();
      
      if (!containerRect || !pageDimensions) return;
      
      const rects = range.getClientRects();
      const relativeRects: {x: number, y: number, w: number, h: number}[] = [];

      const w = pageDimensions.width * scale;
      const h = pageDimensions.height * scale;

      let hasIntersection = false;

      for (let i = 0; i < rects.length; i++) {
          const r = rects[i];
          
          if (r.bottom < containerRect.top || r.top > containerRect.bottom || 
              r.right < containerRect.left || r.left > containerRect.right) {
              continue;
          }

          const relX = r.left - containerRect.left;
          const relY = r.top - containerRect.top;
          
          const x = Math.max(0, relX);
          const y = Math.max(0, relY);
          const rw = Math.min(r.width, w - x);
          const rh = Math.min(r.height, h - y);

          if (rw > 0 && rh > 0) { 
               hasIntersection = true;
               relativeRects.push({
                  x: x / w,
                  y: y / h,
                  w: rw / w,
                  h: rh / h
              });
          }
      }

      if (hasIntersection && relativeRects.length > 0) {
          const newStroke: Stroke = {
              points: [],
              color: colorOverride ?? currentColor,
              width: 0,
              alpha: alphaOverride ?? 0.4,
              type: 'highlight',
              isTextHighlight: true,
              rects: relativeRects
          };
          
          strokesRef.current.push(newStroke);
          drawAnnotations();
          
          if (onStrokesChange) {
              onStrokesChange([...strokesRef.current]);
          }
          selection.removeAllRanges();
      }
  }, [scale, pageDimensions, currentColor, onStrokesChange]);

  // Listen for the "Highlight Now" command — works from both toolbar and selection popup
  useEffect(() => {
      const handler = (e: Event) => {
          const { color, alpha } = (e as CustomEvent).detail ?? {};
          attemptTextHighlight(color, alpha);
      };
      window.addEventListener('REQUEST_TEXT_HIGHLIGHT', handler);
      return () => window.removeEventListener('REQUEST_TEXT_HIGHLIGHT', handler);
  }, [attemptTextHighlight]);

  const performCrop = async () => {
      if (!cropSelection || !canvasRef.current) return;

      const extractTextFromCrop = (cropRect: { x: number; y: number; w: number; h: number }): string => {
          if (!textLayerRef.current || !containerRef.current) return '';
          const containerRect = containerRef.current.getBoundingClientRect();
          const spans = Array.from(textLayerRef.current.querySelectorAll('span'));
          const texts: string[] = [];
          for (const span of spans) {
              const spanRect = span.getBoundingClientRect();
              const relLeft = spanRect.left - containerRect.left;
              const relTop = spanRect.top - containerRect.top;
              const relRight = spanRect.right - containerRect.left;
              const relBottom = spanRect.bottom - containerRect.top;
              const cropRight = cropRect.x + cropRect.w;
              const cropBottom = cropRect.y + cropRect.h;
              if (relLeft < cropRight && relRight > cropRect.x && relTop < cropBottom && relBottom > cropRect.y) {
                  const text = span.textContent?.trim();
                  if (text) texts.push(text);
              }
          }
          return texts.join(' ').replace(/\s+/g, ' ').trim();
      };

      const rect = cropSelection;
      const pixelRatio = window.devicePixelRatio || 1;
      const pageCanvas = canvasRef.current;
      const pageCSSH = pageCanvas.height / pixelRatio;
      const srcX = Math.round(rect.x * pixelRatio);
      const srcY = Math.round(rect.y * pixelRatio);
      const outW = Math.round(rect.w * pixelRatio);

      const drawPage1 = (ctx: CanvasRenderingContext2D, destY: number, srcH: number) => {
          ctx.drawImage(pageCanvas, srcX, srcY, outW, srcH, 0, destY, outW, srcH);
          if (highlightLayerRef.current) {
              ctx.globalCompositeOperation = 'multiply';
              ctx.drawImage(highlightLayerRef.current, srcX, srcY, outW, srcH, 0, destY, outW, srcH);
              ctx.globalCompositeOperation = 'source-over';
          }
          if (annotationRef.current) {
              ctx.drawImage(annotationRef.current, srcX, srcY, outW, srcH, 0, destY, outW, srcH);
          }
      };

      const isCrossPage = rect.y + rect.h > pageCSSH && pdfDoc && pageNumber < pdfDoc.numPages;

      if (!isCrossPage) {
          // Single-page crop (original logic)
          const outH = Math.round(rect.h * pixelRatio);
          const outCanvas = document.createElement('canvas');
          outCanvas.width = outW;
          outCanvas.height = outH;
          const ctx = outCanvas.getContext('2d');
          if (!ctx) return;
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, outW, outH);
          drawPage1(ctx, 0, outH);
          onSnap(outCanvas.toDataURL('image/png'), extractTextFromCrop(rect));
          return;
      }

      // Cross-page: render next page and stitch (skip 16px gap between pages)
      const PAGE_GAP_CSS = 16;
      const page1HCss = pageCSSH - rect.y;
      const page2HCss = (rect.y + rect.h) - pageCSSH - PAGE_GAP_CSS;
      const p1H = Math.round(page1HCss * pixelRatio);
      const p2H = Math.max(0, Math.round(page2HCss * pixelRatio));
      const totalH = p1H + p2H;

      const outCanvas = document.createElement('canvas');
      outCanvas.width = outW;
      outCanvas.height = totalH;
      const ctx = outCanvas.getContext('2d');
      if (!ctx) return;
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, outW, totalH);

      // Draw page 1 bottom portion (with highlights + annotations)
      drawPage1(ctx, 0, p1H);

      // Render page 2 fresh and draw its top portion (with highlight layer if available)
      if (p2H > 0) {
          try {
              const nextPage = await pdfDoc.getPage(pageNumber + 1);
              const viewport = nextPage.getViewport({ scale });
              const nextCanvas = document.createElement('canvas');
              nextCanvas.width = Math.floor(viewport.width * pixelRatio);
              nextCanvas.height = Math.floor(viewport.height * pixelRatio);
              const nextCtx = nextCanvas.getContext('2d');
              if (nextCtx) {
                  const transform = pixelRatio !== 1 ? [pixelRatio, 0, 0, pixelRatio, 0, 0] : null;
                  await nextPage.render({ canvasContext: nextCtx, viewport, transform }).promise;
                  ctx.drawImage(nextCanvas, srcX, 0, outW, p2H, 0, p1H, outW, p2H);
                  // Composite page 2's highlight layer on top (same coords as the live canvas)
                  const nextHl = getHighlightLayerCanvas?.(pageNumber + 1);
                  if (nextHl && nextHl.width > 0 && nextHl.height > 0) {
                      ctx.globalCompositeOperation = 'multiply';
                      ctx.drawImage(nextHl, srcX, 0, outW, p2H, 0, p1H, outW, p2H);
                      ctx.globalCompositeOperation = 'source-over';
                  }
              }
          } catch {
              // Next page render failed — leave that portion white
          }
      }

      onSnap(outCanvas.toDataURL('image/png'), extractTextFromCrop(rect));
  };

  const processEraser = (cx: number, cy: number, w: number, h: number) => {
     const nx = cx / w;
     const ny = cy / h;

     const initialCount = strokesRef.current.length;

     strokesRef.current = strokesRef.current.filter(stroke => {
         if (stroke.isTextHighlight && stroke.rects) {
             return !stroke.rects.some(rect => {
                 return (nx >= rect.x && nx <= rect.x + rect.w && ny >= rect.y && ny <= rect.y + rect.h);
             });
         }
         return !stroke.points.some(p => {
             const px = p.x * w;
             const py = p.y * h;
             const dist = Math.hypot(px - cx, py - cy);
             const threshold = 10; 
             return dist < threshold;
         });
     });

     if (strokesRef.current.length !== initialCount) {
         drawAnnotations();
         if (onStrokesChange) {
             onStrokesChange([...strokesRef.current]);
         }
     }
  };

  const modifySelectedStroke = (updater: (s: Stroke) => Stroke) => {
      if (selectedStrokeIndex === null) return;
      const newStrokes = [...strokesRef.current];
      newStrokes[selectedStrokeIndex] = updater(newStrokes[selectedStrokeIndex]);
      strokesRef.current = newStrokes;
      drawAnnotations();
      if (onStrokesChange) onStrokesChange(newStrokes);
  };

  const deleteSelectedStroke = () => {
      if (selectedStrokeIndex === null) return;
      const newStrokes = strokesRef.current.filter((_, i) => i !== selectedStrokeIndex);
      strokesRef.current = newStrokes;
      setSelectedStrokeIndex(null);
      setMenuPosition(null);
      setHoveredStrokeIndex(null);
      drawAnnotations();
      if (onStrokesChange) onStrokesChange(newStrokes);
  };

  const closeSelectionMenu = () => {
      setSelectedStrokeIndex(null);
      setMenuPosition(null);
      drawAnnotations();
  };

  const copyHighlightText = () => {
      if (!currentSelection?.isTextHighlight || !currentSelection.rects) return;
      if (!textLayerRef.current || !containerRef.current) return;
      const containerRect = containerRef.current.getBoundingClientRect();
      const w = displayWidth;
      const h = displayHeight;
      const spans = Array.from(textLayerRef.current.querySelectorAll('span'));
      const seen = new Set<Element>();
      const texts: string[] = [];
      for (const rect of currentSelection.rects) {
          for (const span of spans) {
              if (seen.has(span)) continue;
              const sr = span.getBoundingClientRect();
              const sl = (sr.left - containerRect.left) / w;
              const st = (sr.top - containerRect.top) / h;
              const sr2 = (sr.right - containerRect.left) / w;
              const sb = (sr.bottom - containerRect.top) / h;
              if (sl < rect.x + rect.w && sr2 > rect.x && st < rect.y + rect.h && sb > rect.y) {
                  const text = span.textContent?.trim();
                  if (text) { texts.push(text); seen.add(span); }
              }
          }
      }
      const text = texts.join(' ').replace(/\s+/g, ' ').trim();
      if (text) {
          navigator.clipboard.writeText(text);
          setCopiedHighlight(true);
          setTimeout(() => setCopiedHighlight(false), 1500);
      }
  };

  const displayWidth = Math.round(pageDimensions ? (pageDimensions.width * scale) : (scale * 600));
  const displayHeight = Math.round(pageDimensions ? (pageDimensions.height * scale) : (scale * 840));
  const currentSelection = selectedStrokeIndex !== null ? strokesRef.current[selectedStrokeIndex] : null;

  return (
    <div
        ref={containerRef}
        data-page-number={pageNumber}
        className="relative bg-white shadow-sm mx-auto origin-top flex-shrink-0"
        style={{
            width: `${displayWidth}px`,
            height: `${displayHeight}px`,
            minHeight: '200px',
            cursor: activeTool === 'move' ? (isPanning.current ? 'grabbing' : 'grab') : undefined,
        }}
        onPointerDown={handleContainerPointerDown}
        onPointerMove={handleContainerPointerMove}
        onPointerUp={handleContainerPointerUp}
        onPointerLeave={handleContainerPointerUp}
        onClick={handleContainerClick}
    >
      <canvas ref={canvasRef} className="block w-full h-full absolute inset-0 z-0" />
      <canvas ref={highlightLayerRef} className="block w-full h-full absolute inset-0 z-[1] mix-blend-multiply pointer-events-none"/>
      <div 
        ref={textLayerRef} 
        className="textLayer"
        style={{ 
            zIndex: (activeTool === 'text_highlight' || activeTool === 'move') ? 2 : 0,
            pointerEvents: (activeTool === 'text_highlight' || activeTool === 'move') ? 'auto' : 'none'
        }}
      ></div>

      <canvas
         ref={annotationRef}
         className={`block w-full h-full absolute inset-0 z-10 ${
            activeTool === 'move' ? 'pointer-events-none' :
            (activeTool === 'text_highlight' && hoveredStrokeIndex === null ? 'pointer-events-none' :
            (hoveredStrokeIndex !== null || activeTool === 'select' ? 'cursor-pointer' : ''))
         }`}
         style={{
             touchAction: (activeTool === 'move' || activeTool === 'text_highlight') ? 'auto' : 'none',
             cursor: activeTool === 'crop' ? 'none' : undefined,
         }}
         onPointerDown={handlePointerDown}
         onPointerMove={handlePointerMove}
         onPointerUp={handlePointerUp}
         onPointerLeave={() => { handlePointerUp(); setCropMousePos(null); }}
      />
      
      {selectedStrokeIndex !== null && menuPosition && currentSelection && (
          <div 
            className="absolute z-50 bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-gray-200 dark:border-slate-700 p-3 flex flex-col gap-3 animate-in fade-in zoom-in-95 duration-200"
            style={{ 
                left: Math.min(Math.max(0, menuPosition.x - 100), displayWidth - 220), 
                top: Math.max(0, menuPosition.y - 220) 
            }}
            onPointerDown={(e) => e.stopPropagation()} 
          >
             <div className="flex justify-between items-center pb-2 border-b dark:border-slate-700 mb-1">
                 <span className="text-[10px] font-bold text-gray-500 uppercase">Edit Stroke</span>
                 <button onClick={closeSelectionMenu} className="p-1 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-full">
                     <X size={12} className="text-gray-500" />
                 </button>
             </div>
             <div className="flex flex-wrap gap-1.5 mb-2">
                 {(currentSelection.type === 'highlight' || currentSelection.isTextHighlight
                   ? [...HIGHLIGHT_COLORS.slice(0, 6), ...customColors]
                   : PEN_COLORS
                 ).map(c => (
                    <button
                        key={c}
                        onPointerDown={e => { e.stopPropagation(); e.preventDefault(); modifySelectedStroke(s => ({ ...s, color: c })); if (currentSelection.type === 'highlight' || currentSelection.isTextHighlight) onSaveHighlightColor(c); }}
                        className={`w-5 h-5 rounded-full border-2 transition-transform hover:scale-110 ${currentSelection.color === c ? 'border-blue-500 scale-110' : 'border-gray-200 dark:border-slate-600'}`}
                        style={{ backgroundColor: c }}
                    />
                 ))}
                 {(currentSelection.type === 'highlight' || currentSelection.isTextHighlight) && (
                   <button
                     className={`w-5 h-5 rounded-full border-2 border-dashed flex items-center justify-center text-[10px] font-bold transition-colors ${showPickerInStroke ? 'border-blue-400 text-blue-500 bg-blue-50' : 'border-gray-300 dark:border-slate-500 text-gray-400 hover:border-gray-500'}`}
                     onPointerDown={e => { e.stopPropagation(); e.preventDefault(); setShowPickerInStroke(v => !v); }}
                     title={language === 'vietnamese' ? 'Màu tùy chỉnh' : 'Custom color'}>+</button>
                 )}
             </div>
             {showPickerInStroke && (currentSelection.type === 'highlight' || currentSelection.isTextHighlight) && (
                 <ColorPickerGrid
                   onSelect={c => { modifySelectedStroke(s => ({ ...s, color: c })); onSaveHighlightColor(c); }}
                   onClose={() => setShowPickerInStroke(false)} />
             )}
             <div className="flex items-center gap-2">
                 <Layers size={14} className="text-gray-500" />
                 <input 
                    type="range" 
                    min="0.1" 
                    max="1.0" 
                    step="0.1"
                    value={currentSelection.alpha}
                    onChange={(e) => modifySelectedStroke(s => ({ ...s, alpha: parseFloat(e.target.value) }))}
                    className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                 />
                 <span className="text-xs w-8 text-right font-mono">{Math.round(currentSelection.alpha * 100)}%</span>
             </div>
             {!currentSelection.isTextHighlight && (
                 <div className="flex items-center gap-2">
                     <MoveHorizontal size={14} className="text-gray-500" />
                     <input 
                        type="range" 
                        min="1" 
                        max={currentSelection.type === 'highlight' ? 50 : 20} 
                        step="1"
                        value={currentSelection.width}
                        onChange={(e) => modifySelectedStroke(s => ({ ...s, width: parseInt(e.target.value) }))}
                        className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                     />
                     <span className="text-xs w-8 text-right font-mono">{currentSelection.width}</span>
                 </div>
             )}
             {currentSelection.isTextHighlight && (
                 <button
                     onClick={copyHighlightText}
                     className="flex items-center justify-center gap-2 w-full py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg text-xs font-bold transition-colors"
                 >
                     {copiedHighlight ? <><Check size={14} /> {language === 'vietnamese' ? 'Đã sao chép!' : 'Copied!'}</> : <><Copy size={14} /> {language === 'vietnamese' ? 'Sao chép văn bản' : 'Copy text'}</>}
                 </button>
             )}
             <button
                onClick={deleteSelectedStroke}
                className="flex items-center justify-center gap-2 w-full py-1.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg text-xs font-bold transition-colors"
             >
                 <Trash2 size={14} /> Delete
             </button>
          </div>
      )}

      {/* Full-screen crosshair lines — visible when in crop mode and mouse is over page */}
      {activeTool === 'crop' && cropMousePos && (
          <div className="absolute inset-0 z-20 pointer-events-none overflow-hidden">
              {/* Vertical line */}
              <div className="absolute top-0 bottom-0 w-px bg-blue-500/70" style={{ left: cropMousePos.x }} />
              {/* Horizontal line */}
              <div className="absolute left-0 right-0 h-px bg-blue-500/70" style={{ top: cropMousePos.y }} />
              {/* Center dot */}
              <div className="absolute w-2 h-2 rounded-full bg-blue-500 -translate-x-1/2 -translate-y-1/2" style={{ left: cropMousePos.x, top: cropMousePos.y }} />
          </div>
      )}

      {/* Spotlight dim overlay — 4 dark rects surrounding the selection, inside stays bright */}
      {cropSelection && cropSelection.w > 4 && cropSelection.h > 4 && (
          <div className="absolute inset-0 z-[19] pointer-events-none">
              <div className="absolute bg-black/40" style={{ left: 0, top: 0, right: 0, height: cropSelection.y }} />
              <div className="absolute bg-black/40" style={{ left: 0, top: cropSelection.y + cropSelection.h, right: 0, bottom: 0 }} />
              <div className="absolute bg-black/40" style={{ left: 0, top: cropSelection.y, width: cropSelection.x, height: cropSelection.h }} />
              <div className="absolute bg-black/40" style={{ left: cropSelection.x + cropSelection.w, top: cropSelection.y, right: 0, height: cropSelection.h }} />
          </div>
      )}

      {/* Crop selection box with corner handles and dimension label */}
      {cropSelection && cropSelection.w > 4 && cropSelection.h > 4 && (
          <div
             className="absolute border-2 border-white z-20 pointer-events-none"
             style={{
                 left: cropSelection.x,
                 top: cropSelection.y,
                 width: cropSelection.w,
                 height: cropSelection.h,
                 boxShadow: '0 0 0 1px rgba(59,130,246,0.8)',
             }}
          >
              {/* Dimension label */}
              <div className="absolute -top-6 left-0 bg-blue-600 text-white text-[10px] font-mono px-1.5 py-0.5 rounded shadow whitespace-nowrap">
                  {Math.round(cropSelection.w)} × {Math.round(cropSelection.h)}
              </div>
              {/* Corner handles */}
              {[
                  { top: -3, left: -3 }, { top: -3, right: -3 },
                  { bottom: -3, left: -3 }, { bottom: -3, right: -3 },
              ].map((pos, i) => (
                  <div key={i} className="absolute w-2.5 h-2.5 bg-white border-2 border-blue-500 rounded-sm" style={pos} />
              ))}
          </div>
      )}
      
      {!isRendered && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-white border border-gray-100">
             <Loader2 className="animate-spin text-gray-300" size={24} />
          </div>
      )}
    </div>
  );
});

// --- MAIN VIEWER ---
export const PdfViewer: React.FC<PdfViewerProps> = ({ url, onSnap, onPageChange, annotations = {}, onAnnotationChange, onDocumentReady, onGuidedReading, guidedReadingHighlights = [], isGuidedReadingLoading = false, language = 'english', userEmail, documentTitle, boardTitle, highlightCaptureButton = false, isMobile = false, onVoiceCapture, selectedNoteName, toolbarPortalTarget }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const innerContainerRef = useRef<HTMLDivElement>(null);

  const [pdfDoc, setPdfDoc] = useState<any>(null);
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1.0); 
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [activeTool, setActiveTool] = useState<ToolType>(() => {
    const saved = localStorage.getItem('pdf_active_tool') as ToolType | null;
    const valid: ToolType[] = ['move', 'select', 'pen', 'highlight', 'eraser', 'text_highlight', 'rect', 'arrow'];
    return saved && valid.includes(saved) ? saved : 'move';
  });
  const [toolbarOpen, setToolbarOpen] = useState(true);
  const [vocabPanelOpen, setVocabPanelOpen] = useState(false);
  const [voiceCaptureOpen, setVoiceCaptureOpen] = useState(false);
  
  const [penWidth, setPenWidth] = useState(3);
  const [highlightWidth, setHighlightWidth] = useState(16);
  const [penColor, setPenColor] = useState('#ef4444');
  const [highlightColor, setHighlightColor] = useState(() => localStorage.getItem('pdf_hl_color') || '#fde047');
  const [highlightAlpha, setHighlightAlpha] = useState(() => parseFloat(localStorage.getItem('pdf_hl_alpha') || '0.5'));
  const [customColors, setCustomColors] = useState<string[]>(() => { try { return JSON.parse(localStorage.getItem('pdf_hl_custom') || '[]'); } catch { return []; } });
  const [showPickerInPopup, setShowPickerInPopup] = useState(false);
  const [pdfDarkMode, setPdfDarkMode] = useState(() => localStorage.getItem('pdf_dark_mode') === 'true');

  const selectTool = (tool: ToolType) => {
    setActiveTool(tool);
    localStorage.setItem('pdf_active_tool', tool);
  };

  const saveHighlightColor = (c: string) => {
    setHighlightColor(c);
    localStorage.setItem('pdf_hl_color', c);
    if (!HIGHLIGHT_COLORS.includes(c)) {
      setCustomColors(prev => {
        const next = [c, ...prev.filter(x => x !== c)].slice(0, 3);
        localStorage.setItem('pdf_hl_custom', JSON.stringify(next));
        return next;
      });
    }
  };

  const saveHighlightAlpha = (a: number) => {
    setHighlightAlpha(a);
    localStorage.setItem('pdf_hl_alpha', String(a));
  };
  const colorInputRef = useRef<HTMLInputElement>(null);

  // Notify parent when page changes (for context-aware AI features)
  const onPageChangeRef = useRef(onPageChange);
  onPageChangeRef.current = onPageChange;
  React.useEffect(() => { onPageChangeRef.current?.(currentPage); }, [currentPage]);

  // PDF keyword search
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchMatchPages, setSearchMatchPages] = useState<number[]>([]);
  const [searchMatchIdx, setSearchMatchIdx] = useState(0);
  const [searchScanning, setSearchScanning] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // TOC state
  const [outline, setOutline] = useState<OutlineItem[]>([]);
  const [tocOpen, setTocOpen] = useState(false);
  const [debugHidden, setDebugHidden] = useState(() => localStorage.getItem('pdf_debug_hidden') === 'true');

  // Virtual rendering — page dimensions at scale=1 (used to compute placeholder heights)
  const [pageNaturalDims, setPageNaturalDims] = useState<{width: number; height: number} | null>(null);
  const [visibleRange, setVisibleRange] = useState({ start: 1, end: 8 });


  const pageCanvases = useRef<Map<number, HTMLCanvasElement>>(new Map());
  const pageHighlightLayerRefs = useRef<Map<number, HTMLCanvasElement>>(new Map());
  const textLayerRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const annotationData = useRef<Map<number, () => Stroke[]>>(new Map());

  // Stable localStorage key — extract the object path from Firebase Storage URL so the
  // volatile `token=` query param doesn't break key matching between sessions.
  const pdfStateKey = useMemo(() => {
    try {
      const pathMatch = url.match(/\/o\/([^?#]+)/);
      if (pathMatch) {
        const stablePath = decodeURIComponent(pathMatch[1]).slice(-60);
        return `pdf_state_${stablePath.replace(/[^a-zA-Z0-9/_.-]/g, '_')}`;
      }
    } catch {}
    return `pdf_state_${url.split('?')[0].slice(-60).replace(/[^a-zA-Z0-9/_.-]/g, '_')}`;
  }, [url]);
  // scrollTop to restore after PDF loads (state, not ref, so the useEffect below can react to it)
  const [scrollRestoreTarget, setScrollRestoreTarget] = useState<number | null>(null);
  const scrollSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const getPageText = async (pageNum?: number): Promise<string> => {
    if (!pdfDoc) return '';
    const page = await pdfDoc.getPage(pageNum ?? currentPage);
    const content = await page.getTextContent();
    return content.items.map((item: any) => item.str).join(' ');
  };

  const getPageThumbnail = async (pageNum: number = 1): Promise<string> => {
    if (!pdfDoc) return '';
    const page = await pdfDoc.getPage(pageNum);
    const viewport = page.getViewport({ scale: 0.5 });
    const offscreen = document.createElement('canvas');
    offscreen.width = viewport.width;
    offscreen.height = viewport.height;
    const ctx = offscreen.getContext('2d');
    if (!ctx) return '';
    await page.render({ canvasContext: ctx, viewport }).promise;
    return offscreen.toDataURL('image/jpeg', 0.7);
  };

  const captureCurrentPage = async (pageNum: number): Promise<string> => {
    if (!pdfDoc) return '';
    const page = await pdfDoc.getPage(pageNum);
    const viewport = page.getViewport({ scale: 1.5 });
    const offscreen = document.createElement('canvas');
    offscreen.width = viewport.width;
    offscreen.height = viewport.height;
    const ctx = offscreen.getContext('2d');
    if (!ctx) return '';
    await page.render({ canvasContext: ctx, viewport }).promise;
    return offscreen.toDataURL('image/jpeg', 0.88);
  };

  const captureSegment = async (
    pageNum: number,
    startText: string,
    endText: string,
    keyHighlights: string[],
    colorIndex: number
  ): Promise<string> => {
    const canvas = pageCanvases.current.get(pageNum);
    const textLayer = textLayerRefs.current.get(pageNum);
    if (!canvas || !textLayer) return captureCurrentPage(pageNum);

    const spans = Array.from(textLayer.querySelectorAll('span'));
    if (spans.length === 0) return captureCurrentPage(pageNum);

    // Build position map (same algorithm as highlight useEffect)
    const spanData: { span: Element; start: number; end: number }[] = [];
    let pos = 0;
    for (const span of spans) {
      const len = (span.textContent || '').length;
      spanData.push({ span, start: pos, end: pos + len });
      pos += len + 1;
    }
    const fullText = spanData.map(d => d.span.textContent || '').join(' ').toLowerCase();

    const findIdx = (phrase: string, fromEnd = false): number => {
      const lower = phrase.toLowerCase().trim();
      let idx = fromEnd ? fullText.lastIndexOf(lower) : fullText.indexOf(lower);
      if (idx === -1) {
        const words = lower.split(/\s+/);
        const candidates = fromEnd ? [...words].reverse() : words;
        const word = candidates.find(w => w.length >= 5);
        if (word) idx = fromEnd ? fullText.lastIndexOf(word) : fullText.indexOf(word);
      }
      return idx;
    };

    const startIdx = findIdx(startText, false);
    const endMatchIdx = findIdx(endText, true);
    if (startIdx === -1) return captureCurrentPage(pageNum);
    const segEnd = endMatchIdx !== -1 ? endMatchIdx + endText.trim().length : fullText.length;

    const segSpans = spanData.filter(d => d.end > startIdx && d.start < segEnd);
    if (segSpans.length === 0) return captureCurrentPage(pageNum);

    // Bounding boxes in CSS pixels relative to canvas
    const canvasRect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / (canvasRect.width || 1);
    const scaleY = canvas.height / (canvasRect.height || 1);

    let minY = Infinity, maxY = -Infinity;
    for (const { span } of segSpans) {
      const r = span.getBoundingClientRect();
      minY = Math.min(minY, r.top - canvasRect.top);
      maxY = Math.max(maxY, r.bottom - canvasRect.top);
    }

    const padCSS = 28;
    const cropYcss = Math.max(0, minY - padCSS);
    const cropHcss = Math.min(canvasRect.height - cropYcss, maxY - cropYcss + padCSS);
    const cropY = Math.round(cropYcss * scaleY);
    const cropH = Math.round(cropHcss * scaleY);
    if (cropH <= 0) return captureCurrentPage(pageNum);

    // Create output canvas (full width, cropped height)
    const out = document.createElement('canvas');
    out.width = canvas.width;
    out.height = cropH;
    const ctx = out.getContext('2d');
    if (!ctx) return captureCurrentPage(pageNum);

    // Draw cropped region from the page canvas
    ctx.drawImage(canvas, 0, cropY, canvas.width, cropH, 0, 0, canvas.width, cropH);

    // Option C: segment tint + left bar + very light phrase highlights
    // Colors indexed by colorIndex: amber / sky / emerald / purple
    const segBg = [
      'rgba(251,191,36,0.06)',
      'rgba(56,189,248,0.05)',
      'rgba(52,211,153,0.05)',
      'rgba(216,180,254,0.06)',
    ][colorIndex % 4];
    const barColor = [
      'rgba(245,158,11,0.70)',
      'rgba(14,165,233,0.65)',
      'rgba(16,185,129,0.65)',
      'rgba(168,85,247,0.70)',
    ][colorIndex % 4];
    const phraseColor = [
      'rgba(251,191,36,0.30)',
      'rgba(56,189,248,0.26)',
      'rgba(52,211,153,0.26)',
      'rgba(216,180,254,0.30)',
    ][colorIndex % 4];

    // 1. Light tint over whole cropped area
    ctx.fillStyle = segBg;
    ctx.fillRect(0, 0, out.width, out.height);

    // 2. Left bar (4px)
    const barW = Math.round(4 * scaleX);
    ctx.fillStyle = barColor;
    ctx.fillRect(0, 0, barW, out.height);

    // 3. Very light phrase highlights (max 3 phrases to keep image clean)
    const phrasesToHighlight = keyHighlights.slice(0, 3);
    for (const phrase of phrasesToHighlight) {
      const lower = phrase.toLowerCase().trim();
      let pIdx = fullText.indexOf(lower);
      if (pIdx === -1) {
        const w = lower.split(/\s+/).find(w => w.length >= 5);
        if (w) pIdx = fullText.indexOf(w);
      }
      if (pIdx === -1) continue;
      const pEnd = pIdx + lower.length;

      for (const { span, start, end } of spanData) {
        if (end > pIdx && start < pEnd) {
          const r = span.getBoundingClientRect();
          const x = (r.left - canvasRect.left) * scaleX;
          const y = (r.top - canvasRect.top) * scaleY - cropY;
          const w = r.width * scaleX;
          const h = r.height * scaleY;
          ctx.fillStyle = phraseColor;
          ctx.fillRect(x, y, w, h);
        }
      }
    }

    return out.toDataURL('image/jpeg', 0.92);
  };

  // --- PINCH TO ZOOM STATE ---
  const pinchRef = useRef<{
    isPinching: boolean,
    startDist: number,
    startScale: number,
    startScrollTop: number,
    startScrollLeft: number,
    centerX: number,
    centerY: number
  }>({
    isPinching: false,
    startDist: 0,
    startScale: 1,
    startScrollTop: 0,
    startScrollLeft: 0,
    centerX: 0,
    centerY: 0
  });

  const pendingScrollRef = useRef<{x: number, y: number} | null>(null);

  useEffect(() => {
     try {
         const saved = localStorage.getItem('ms_custom_colors');
         if (saved) setCustomColors(JSON.parse(saved));
     } catch(e) {}
  }, []);

  const saveCustomColor = (color: string) => {
     const newColors = [color, ...customColors.filter(c => c !== color)].slice(0, 12);
     setCustomColors(newColors);
     localStorage.setItem('ms_custom_colors', JSON.stringify(newColors));
  };

  const handleColorPick = (e: React.ChangeEvent<HTMLInputElement>) => {
      const color = e.target.value;
      if (activeTool === 'highlight' || activeTool === 'text_highlight') setHighlightColor(color);
      else setPenColor(color);
      saveCustomColor(color);
  };
  
  const triggerTextHighlight = (color?: string, alpha?: number) => {
      const event = new CustomEvent('REQUEST_TEXT_HIGHLIGHT', { detail: { color, alpha } });
      window.dispatchEvent(event);
  };

  // ── Selection Popup (Adobe-style) ──────────────────────────────────────
  const [selectionPopup, setSelectionPopup] = useState<{ x: number; y: number } | null>(null);
  React.useEffect(() => { if (!selectionPopup) setShowPickerInPopup(false); }, [selectionPopup]);

  const activeToolRef = useRef(activeTool);
  activeToolRef.current = activeTool;
  const highlightColorRef = useRef(highlightColor);
  highlightColorRef.current = highlightColor;
  const highlightAlphaRef = useRef(highlightAlpha);
  highlightAlphaRef.current = highlightAlpha;

  React.useEffect(() => {
      let mouseIsDown = false;
      let pendingTimer: ReturnType<typeof setTimeout> | null = null;
      let popupProtected = false; // blocks mousedown from clearing popup briefly after selection

      const getValidPdfSelection = (): DOMRect | null => {
          const sel = window.getSelection();
          if (!sel || sel.isCollapsed || sel.rangeCount === 0) return null;
          const anchorNode = sel.anchorNode;
          if (!containerRef.current || !anchorNode || !containerRef.current.contains(anchorNode)) return null;
          const rect = sel.getRangeAt(0).getBoundingClientRect();
          if (rect.width < 2) return null;
          return rect;
      };

      const onMouseDown = (e: MouseEvent) => {
          const popup = document.getElementById('pdf-selection-popup');
          if (popup?.contains(e.target as Node)) return;
          if (popupProtected) { mouseIsDown = true; return; }
          mouseIsDown = true;
          setSelectionPopup(null);
      };

      const onMouseUp = () => {
          mouseIsDown = false;
          if (pendingTimer) clearTimeout(pendingTimer);
          const tool = activeToolRef.current;
          if (tool !== 'text_highlight' && tool !== 'move') return;

          // Capture selection synchronously at mouseup — before browser's click event
          // collapses the selection (click fires ~0ms after mouseup)
          const rect = getValidPdfSelection();
          if (!rect) return;

          if (tool === 'text_highlight') {
              window.dispatchEvent(new CustomEvent('REQUEST_TEXT_HIGHLIGHT', {
                  detail: { color: highlightColorRef.current, alpha: highlightAlphaRef.current }
              }));
          } else {
              const popupX = rect.left + rect.width / 2;
              const popupY = rect.top;
              popupProtected = true;
              pendingTimer = setTimeout(() => {
                  setSelectionPopup({ x: popupX, y: popupY });
                  setTimeout(() => { popupProtected = false; }, 250);
              }, 120);
          }
      };

      // No selectionchange listener — it fires after click and collapses selection,
      // which would immediately clear the popup before the user can interact with it.
      document.addEventListener('mousedown', onMouseDown);
      document.addEventListener('mouseup', onMouseUp);
      return () => {
          document.removeEventListener('mousedown', onMouseDown);
          document.removeEventListener('mouseup', onMouseUp);
          if (pendingTimer) clearTimeout(pendingTimer);
      };
  }, []);

  const handleContainerMouseDown = useCallback((_e: React.MouseEvent) => {
      // No-op: document-level handler manages popup lifecycle
  }, []);
  
  const handleHighlightToolClick = () => {
      if (activeTool === 'text_highlight') {
          // If already active, the button acts as the "Highlight" trigger
          triggerTextHighlight();
      } else {
          setActiveTool('text_highlight');
      }
  };

  useEffect(() => {
    const loadPdf = async () => {
      setLoading(true);
      setError(null);
      setNumPages(0);
      setOutline([]);
      setTocOpen(false);
      pageCanvases.current.clear();
      annotationData.current.clear();
      
      try {
        const pdfjsLib = (window as any).pdfjsLib;
        if (!pdfjsLib) throw new Error("PDF Library not loaded");
        // isEvalSupported:false blocks CVE-2024-4367 (a crafted PDF font running JS in our page) in pdf.js < 4.2.67.
        const loadingTask = pdfjsLib.getDocument({ url, isEvalSupported: false });
        const doc = await loadingTask.promise;

        // Load PDF outline (TOC) — resolves each dest to a 1-based page number
        try {
          const rawOutline = await doc.getOutline();
          if (rawOutline && rawOutline.length > 0) {
            const resolveItems = async (items: any[], level: number, prefix: string): Promise<OutlineItem[]> => {
              const result: OutlineItem[] = [];
              for (let i = 0; i < items.length; i++) {
                const item = items[i];
                let pageNum = 1;
                try {
                  let dest = item.dest;
                  if (typeof dest === 'string') dest = await doc.getDestination(dest);
                  if (dest && dest[0]) pageNum = (await doc.getPageIndex(dest[0])) + 1;
                } catch { /* keep pageNum = 1 */ }
                const key = `${prefix}-${i}`;
                const children = item.items?.length ? await resolveItems(item.items, level + 1, key) : [];
                result.push({ title: item.title || '(Untitled)', pageNumber: pageNum, level, key, children });
              }
              return result;
            };
            setOutline(await resolveItems(rawOutline, 0, 'o'));
          }
        } catch (e) {
          console.warn('PDF outline not available', e);
        }

        const page1 = await doc.getPage(1);
        const vp = page1.getViewport({ scale: 1 });
        setPageNaturalDims({ width: vp.width, height: vp.height });
        // Restore saved zoom + scroll, or fall back to fit-width
        let savedState: { scale?: number; scrollTop?: number; page?: number } = {};
        try { savedState = JSON.parse(localStorage.getItem(pdfStateKey) || '{}'); } catch {}
        const containerWidth = (() => {
            if (!containerRef.current) return 600;
            const el = containerRef.current;
            const cs = getComputedStyle(el);
            return el.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
        })();
        const fitScale = Math.min(Math.max(containerWidth / vp.width, 0.5), 2.0);
        const restoredScale = (savedState.scale && savedState.scale >= 0.5 && savedState.scale <= 3.0)
            ? savedState.scale : fitScale;
        if (savedState.page && savedState.page > 1) {
            // Recalculate pixel from page number — immune to container width/scale drift
            const pageH = Math.round(vp.height * restoredScale) + 16; // 16 = PAGE_GAP
            const targetScrollTop = (savedState.page - 1) * pageH;
            const totalPages = doc.numPages;
            setVisibleRange({
                start: Math.max(1, savedState.page - 3),
                end: Math.min(totalPages, savedState.page + 8),
            });
            setScrollRestoreTarget(targetScrollTop);
        } else if (savedState.scrollTop && savedState.scrollTop > 0) {
            setScrollRestoreTarget(savedState.scrollTop);
        }
        // Set these together with setScale so React batches them into one render.
        // This prevents the persist effect from firing with pdfDoc != null but scale still at 1.0
        // (which would overwrite the saved zoom level with 100% before restoration completes).
        setPdfDoc(doc);
        setNumPages(doc.numPages);
        setCurrentPage(1);
        setScale(restoredScale);

      } catch (err: any) {
        console.error("Error loading PDF:", err);
        setError("Failed to load PDF.");
      } finally {
        setLoading(false);
      }
    };
    if (url) loadPdf();
  }, [url]);

  const handleSetPageRef = useCallback((pageNum: number, canvas: HTMLCanvasElement | null) => {
      if (canvas) pageCanvases.current.set(pageNum, canvas);
      else pageCanvases.current.delete(pageNum);
  }, []);

  const handleSetHighlightLayerRef = useCallback((pageNum: number, canvas: HTMLCanvasElement | null) => {
      if (canvas) pageHighlightLayerRefs.current.set(pageNum, canvas);
      else pageHighlightLayerRefs.current.delete(pageNum);
  }, []);

  const getHighlightLayerCanvas = useCallback((pageNum: number) => {
      return pageHighlightLayerRefs.current.get(pageNum) ?? null;
  }, []);

  const handleSetTextLayerRef = useCallback((pageNum: number, el: HTMLDivElement | null) => {
      if (el) textLayerRefs.current.set(pageNum, el);
      else textLayerRefs.current.delete(pageNum);
  }, []);
  
  const registerAnnotationRef = useCallback((pageNum: number, getter: () => Stroke[]) => {
      annotationData.current.set(pageNum, getter);
  }, []);

  const handleStrokesChange = useCallback((pageNum: number, strokes: Stroke[]) => {
      if (onAnnotationChange) onAnnotationChange(pageNum, strokes);
  }, [onAnnotationChange]);

  const jumpToPage = useCallback((pageNum: number) => {
    if (!containerRef.current) return;
    setCurrentPage(pageNum);
    if (pageNaturalDims) {
        const pageH = Math.round(pageNaturalDims.height * scale) + PAGE_GAP;
        setVisibleRange({ start: Math.max(1, pageNum - VIRTUAL_BUFFER), end: Math.min(numPages, pageNum + VIRTUAL_BUFFER + 5) });
        const targetScrollTop = (pageNum - 1) * pageH;
        containerRef.current.scrollTo({ top: targetScrollTop, behavior: 'smooth' });
        // Save immediately — smooth scroll over large distances may not fire reliable
        // scroll events in all browsers, so we can't rely solely on the debounced handleScroll.
        try {
            const existing = JSON.parse(localStorage.getItem(pdfStateKey) || '{}');
            localStorage.setItem(pdfStateKey, JSON.stringify({ ...existing, scrollTop: targetScrollTop, page: pageNum }));
        } catch {}
    } else {
        const pageEl = containerRef.current.querySelector(`[data-page-number="${pageNum}"]`);
        if (pageEl) pageEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [pageNaturalDims, scale, numPages, pdfStateKey]);

  const flatOutline = useMemo(() => {
    const flatten = (items: OutlineItem[]): OutlineItem[] =>
      items.flatMap(item => [item, ...flatten(item.children)]);
    return flatten(outline);
  }, [outline]);

  const activeOutlineKey = useMemo(() => {
    if (flatOutline.length === 0) return null;
    let active = flatOutline[0];
    for (const item of flatOutline) {
      if (item.pageNumber <= currentPage) active = item;
      else break;
    }
    return active.key;
  }, [flatOutline, currentPage]);

  const PAGE_GAP = 16; // mb-4 = 16px between pages
  const VIRTUAL_BUFFER = 3; // render 3 pages above/below viewport
  // Integer page height used for all scroll calculations — must match Math.round(phHeight)
  const PAGE_H = pageNaturalDims ? Math.round(pageNaturalDims.height * scale) + PAGE_GAP : scale * 840 + PAGE_GAP;

  const handleScroll = useCallback(() => {
    if (!containerRef.current || pinchRef.current.isPinching) return;
    setSelectionPopup(null);
    const { scrollTop, clientHeight } = containerRef.current;

    // Debounced scroll position save (500ms)
    if (scrollSaveTimerRef.current) clearTimeout(scrollSaveTimerRef.current);
    scrollSaveTimerRef.current = setTimeout(() => {
      try {
        const existing = JSON.parse(localStorage.getItem(pdfStateKey) || '{}');
        localStorage.setItem(pdfStateKey, JSON.stringify({ ...existing, scrollTop, page: currentPage }));
      } catch {}
    }, 500);

    if (pageNaturalDims) {
        // Update visible range for virtual rendering
        const start = Math.max(1, Math.floor(scrollTop / PAGE_H) - VIRTUAL_BUFFER + 1);
        const end = Math.min(numPages, Math.ceil((scrollTop + clientHeight) / PAGE_H) + VIRTUAL_BUFFER);
        setVisibleRange(prev => (prev.start === start && prev.end === end) ? prev : { start, end });
        // Estimate current page from scroll position
        const centerScrollY = scrollTop + clientHeight / 2;
        const estimated = Math.max(1, Math.min(numPages, Math.ceil(centerScrollY / PAGE_H)));
        if (estimated !== currentPage) setCurrentPage(estimated);
    } else {
        // Fallback: DOM query (before page dims are known)
        const container = containerRef.current;
        const containerRect = container.getBoundingClientRect();
        const centerY = containerRect.top + containerRect.height / 2;
        const pageEls = container.querySelectorAll('[data-page-number]');
        let closestPage = currentPage;
        let minDist = Infinity;
        pageEls.forEach((el) => {
            const rect = el.getBoundingClientRect();
            if (rect.bottom < containerRect.top || rect.top > containerRect.bottom) return;
            const dist = Math.abs(centerY - (rect.top + rect.height / 2));
            if (dist < minDist) { minDist = dist; closestPage = Number(el.getAttribute('data-page-number')); }
        });
        if (closestPage !== currentPage) setCurrentPage(closestPage);
    }
  }, [currentPage, pageNaturalDims, scale, numPages]);

  // --- TRACKPAD PINCH-TO-ZOOM (wheel + ctrlKey) ---
  // React synthetic onWheel cannot call e.preventDefault() effectively (React 17+ uses root
  // delegation, so the browser's native page zoom fires before the handler runs).
  // Fix: native listener with { passive: false } solely to block browser zoom; all zoom
  // logic stays in the React synthetic handler below so nothing else changes.
  // Two-finger trackpad pinch is disabled — scroll compensation with rapid batched events
  // causes uncontrolled position jumps. Users should use the zoom buttons instead.
  // The native listener still blocks browser page zoom so the PDF doesn't get distorted.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const blockNativeZoom = (e: WheelEvent) => { if (e.ctrlKey) e.preventDefault(); };
    el.addEventListener('wheel', blockNativeZoom, { passive: false });
    return () => el.removeEventListener('wheel', blockNativeZoom);
  }, []);

  const handleWheel = useCallback((_e: React.WheelEvent) => {
    // intentionally disabled — see comment above
  }, []);

  // Zoom keeping viewport center fixed (used by +/- buttons)
  const zoomAtViewportCenter = useCallback((delta: number) => {
    if (!containerRef.current) return;
    const { scrollLeft, scrollTop, clientWidth, clientHeight } = containerRef.current;
    const focalX = scrollLeft + clientWidth / 2;
    const focalY = scrollTop + clientHeight / 2;
    setScale(s => {
      const newScale = Math.min(Math.max(0.3, s + delta), 4.0);
      const ratio = newScale / s;
      pendingScrollRef.current = {
        x: focalX * ratio - clientWidth / 2,
        y: focalY * ratio - clientHeight / 2,
      };
      return newScale;
    });
  }, []);

  // --- PINCH TO ZOOM LOGIC (touch screen) ---

  const handleTouchStart = (e: React.TouchEvent) => {
    // DISABLE custom touch listeners for text highlight mode to allow native selection
    if (activeTool === 'text_highlight') return;

    if (e.touches.length === 2) {
      e.preventDefault(); // Prevent browser zoom
      
      const t1 = e.touches[0];
      const t2 = e.touches[1];
      const dist = Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY);
      
      if (!containerRef.current || !innerContainerRef.current) return;

      const rect = innerContainerRef.current.getBoundingClientRect();
      const containerRect = containerRef.current.getBoundingClientRect();
      
      // Calculate center relative to the inner content
      // Note: We use relative to screen for calculating start, but relative to inner for visual origin
      const centerX = (t1.clientX + t2.clientX) / 2;
      const centerY = (t1.clientY + t2.clientY) / 2;
      
      // Center point relative to the inner content (the thing being scaled)
      const contentX = centerX - rect.left;
      const contentY = centerY - rect.top;

      pinchRef.current = {
        isPinching: true,
        startDist: dist,
        startScale: scale,
        startScrollTop: containerRef.current.scrollTop,
        startScrollLeft: containerRef.current.scrollLeft,
        centerX: contentX,
        centerY: contentY
      };
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (activeTool === 'text_highlight') return;
    if (!pinchRef.current.isPinching || e.touches.length !== 2) return;
    e.preventDefault();

    const t1 = e.touches[0];
    const t2 = e.touches[1];
    const dist = Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY);
    
    // Scale Factor relative to start of gesture
    const scaleFactor = dist / pinchRef.current.startDist;

    if (innerContainerRef.current) {
        // Apply CSS Transform for smooth 60fps zooming
        const { centerX, centerY } = pinchRef.current;
        innerContainerRef.current.style.transformOrigin = `${centerX}px ${centerY}px`;
        innerContainerRef.current.style.transform = `scale(${scaleFactor})`;
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (activeTool === 'text_highlight') return;
    if (pinchRef.current.isPinching && e.touches.length < 2) {
      if (innerContainerRef.current && containerRef.current) {
         // Get the visual scale factor from the CSS transform we applied
         const matrix = new WebKitCSSMatrix(window.getComputedStyle(innerContainerRef.current).transform);
         const visualScaleFactor = matrix.a; // Scale X
         
         const newScale = Math.min(Math.max(0.3, pinchRef.current.startScale * visualScaleFactor), 4.0);
         
         // Calculate the ratio of change
         const ratio = newScale / pinchRef.current.startScale;

         // Calculate new scroll position to keep focal point stable
         const { centerX, centerY, startScrollLeft, startScrollTop } = pinchRef.current;
         
         const newScrollLeft = centerX * (ratio - 1) + startScrollLeft;
         const newScrollTop = centerY * (ratio - 1) + startScrollTop;
         
         pendingScrollRef.current = { x: newScrollLeft, y: newScrollTop };

         // Reset CSS
         innerContainerRef.current.style.transform = '';
         innerContainerRef.current.style.transformOrigin = '';
         
         // Trigger React State Update (High Quality Render)
         setScale(newScale);
      }
      pinchRef.current.isPinching = false;
    }
  };

  // Apply pending scroll after render (zoom gestures)
  useLayoutEffect(() => {
     if (pendingScrollRef.current && containerRef.current) {
         containerRef.current.scrollLeft = pendingScrollRef.current.x;
         containerRef.current.scrollTop = pendingScrollRef.current.y;
         pendingScrollRef.current = null;
     }
  }, [scale]); // Runs after scale updates layout

  // Restore saved scroll position after PDF is fully laid out.
  // Retries each rAF until scrollTop actually reaches target (handles virtual rendering layout delays).
  useEffect(() => {
    if (scrollRestoreTarget === null || !containerRef.current || !pageNaturalDims) return;
    const target = scrollRestoreTarget;
    setScrollRestoreTarget(null);
    let attempts = 0;
    const tryScroll = () => {
      if (!containerRef.current || attempts > 20) return;
      containerRef.current.scrollTop = target;
      attempts++;
      if (Math.abs(containerRef.current.scrollTop - target) > 10) {
        requestAnimationFrame(tryScroll);
      }
    };
    requestAnimationFrame(tryScroll);
  }, [scrollRestoreTarget, pageNaturalDims]);

  // Recalculate visible range when scale changes (page heights change)
  useEffect(() => {
    handleScroll();
  }, [scale]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-center horizontal scroll whenever container or content changes size
  useEffect(() => {
    const container = containerRef.current;
    const inner = innerContainerRef.current;
    if (!container || !inner) return;

    const centerH = () => {
      const overflow = container.scrollWidth - container.clientWidth;
      container.scrollLeft = overflow > 0 ? overflow / 2 : 0;
    };

    // Run immediately after paint (PDF may already be wider than container)
    const raf = requestAnimationFrame(centerH);

    // Re-center when panel is resized OR when PDF page renders at new scale
    const obs1 = new ResizeObserver(() => requestAnimationFrame(centerH));
    const obs2 = new ResizeObserver(() => requestAnimationFrame(centerH));
    obs1.observe(container);
    obs2.observe(inner);

    return () => {
      cancelAnimationFrame(raf);
      obs1.disconnect();
      obs2.disconnect();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Notify parent when document is ready (fires once per PDF load)
  const onDocumentReadyRef = useRef(onDocumentReady);
  onDocumentReadyRef.current = onDocumentReady;
  useEffect(() => {
    if (!pdfDoc) return;
    onDocumentReadyRef.current?.({ getPageText, getPageThumbnail, captureCurrentPage, captureSegment });
  }, [pdfDoc]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist zoom level whenever it changes (after PDF is loaded)
  useEffect(() => {
    if (!pdfDoc) return;
    try {
      const existing = JSON.parse(localStorage.getItem(pdfStateKey) || '{}');
      localStorage.setItem(pdfStateKey, JSON.stringify({ ...existing, scale }));
    } catch {}
  }, [scale, pdfStateKey, pdfDoc]);
  
  // Cmd+F / Ctrl+F opens PDF search
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'f') {
        // An expanded note on top owns Ctrl+F (its own find bar); don't open PDF search behind it.
        if (document.body.dataset.expandedNoteOpen) return;
        if (!containerRef.current?.contains(document.activeElement) && document.activeElement !== document.body) return;
        e.preventDefault();
        setSearchOpen(true);
        setTimeout(() => searchInputRef.current?.focus(), 50);
      }
      if (e.key === 'Escape' && searchOpen) {
        setSearchOpen(false);
        setSearchQuery('');
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [searchOpen]);

  // Scan all pages for keyword matches (debounced)
  useEffect(() => {
    if (!pdfDoc || !searchQuery.trim()) {
      setSearchMatchPages([]);
      setSearchScanning(false);
      return;
    }
    const q = searchQuery.toLowerCase().trim();
    let cancelled = false;
    const timer = setTimeout(async () => {
      setSearchScanning(true);
      const matchPages: number[] = [];
      for (let p = 1; p <= numPages; p++) {
        if (cancelled) break;
        try {
          const text = await getPageText(p);
          if (text.toLowerCase().includes(q)) matchPages.push(p);
        } catch {}
      }
      if (!cancelled) {
        setSearchMatchPages(matchPages);
        setSearchMatchIdx(0);
        setSearchScanning(false);
        if (matchPages.length > 0) jumpToPage(matchPages[0]);
      }
    }, 500);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [searchQuery, pdfDoc, numPages]);

  // Apply yellow highlight to matching spans in visible text layers
  useEffect(() => {
    textLayerRefs.current.forEach(div => {
      div.querySelectorAll('[data-pdf-search-hl]').forEach(el => {
        const span = el as HTMLElement;
        span.removeAttribute('data-pdf-search-hl');
        span.style.removeProperty('background-color');
        span.style.removeProperty('outline');
      });
      if (!searchQuery.trim() || !searchOpen) return;
      const q = searchQuery.toLowerCase().trim();
      div.querySelectorAll('span').forEach(span => {
        if (span.querySelector('span')) return;
        if ((span.textContent || '').toLowerCase().includes(q)) {
          span.dataset.pdfSearchHl = '1';
          span.style.backgroundColor = '#fbbf24';
          span.style.outline = '1px solid #f59e0b';
        }
      });
    });
  }, [searchQuery, searchOpen, visibleRange]);

  const searchNavigate = useCallback((dir: 'next' | 'prev') => {
    if (!searchMatchPages.length) return;
    const newIdx = dir === 'next'
      ? (searchMatchIdx + 1) % searchMatchPages.length
      : (searchMatchIdx - 1 + searchMatchPages.length) % searchMatchPages.length;
    setSearchMatchIdx(newIdx);
    jumpToPage(searchMatchPages[newIdx]);
  }, [searchMatchPages, searchMatchIdx, jumpToPage]);

  const activeColor = activeTool === 'highlight' || activeTool === 'text_highlight' ? highlightColor : penColor;
  const activePalette = (activeTool === 'highlight' || activeTool === 'text_highlight') ? HIGHLIGHT_COLORS : PEN_COLORS;
  const pages = useMemo(() => Array.from({ length: numPages }, (_, i) => i + 1), [numPages]);

  if (loading) return <div className="flex flex-col items-center justify-center h-full text-gray-500 bg-gray-50/50"><Loader2 size={32} className="animate-spin text-blue-500" /></div>;
  if (error) return <div className="flex flex-col items-center justify-center h-full text-red-500 p-8 text-center"><AlertCircle size={32} className="mb-4" /><p>{error}</p></div>;

  const renderTocItems = (items: OutlineItem[]): React.ReactNode =>
    items.map(item => (
      <React.Fragment key={item.key}>
        <button
          onClick={() => jumpToPage(item.pageNumber)}
          className={`w-full text-left py-1.5 pr-3 text-xs flex items-center gap-1 transition-colors ${
            activeOutlineKey === item.key
              ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-semibold'
              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700/60'
          }`}
          style={{ paddingLeft: `${12 + Math.min(item.level, 5) * 12}px` }}
        >
          {item.children.length > 0 && <ChevronRight size={10} className="flex-shrink-0 opacity-40" />}
          <span className="truncate flex-1">{item.title}</span>
          <span className="text-[10px] text-gray-400 dark:text-gray-500 flex-shrink-0 ml-1">{item.pageNumber}</span>
        </button>
        {renderTocItems(item.children)}
      </React.Fragment>
    ));

  return (
    <div className="flex flex-col h-full bg-gray-200 dark:bg-slate-900 relative">
      <style>{`
        .ai-hl-0 { background: rgba(250,204,21,0.75); border-radius: 2px; }
        .ai-hl-1 { background: rgba(56,189,248,0.65); border-radius: 2px; }
        .ai-hl-2 { background: rgba(52,211,153,0.65); border-radius: 2px; }
        .ai-hl-3 { background: rgba(216,180,254,0.75); border-radius: 2px; }
      `}</style>

      {/* PDF keyword search bar */}
      {searchOpen && (
        <div className="absolute top-2 right-4 z-50 flex items-center gap-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-xl shadow-xl px-3 py-2">
          <Search size={13} className="text-gray-400 flex-shrink-0" />
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') searchNavigate(e.shiftKey ? 'prev' : 'next');
              if (e.key === 'Escape') { setSearchOpen(false); setSearchQuery(''); }
            }}
            placeholder={language === 'vietnamese' ? 'Tìm trong PDF...' : 'Search in PDF...'}
            className="w-44 text-sm bg-transparent outline-none text-gray-800 dark:text-gray-100 placeholder:text-gray-400"
          />
          {searchQuery.trim() && (
            <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0 min-w-[60px] text-center">
              {searchScanning
                ? <Loader2 size={11} className="animate-spin inline" />
                : searchMatchPages.length > 0
                  ? `${searchMatchIdx + 1}/${searchMatchPages.length}`
                  : (language === 'vietnamese' ? 'Không có' : 'No match')}
            </span>
          )}
          <button onClick={() => searchNavigate('prev')} disabled={!searchMatchPages.length} className="p-0.5 rounded hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-30 text-gray-500 transition-colors"><ChevronUp size={14} /></button>
          <button onClick={() => searchNavigate('next')} disabled={!searchMatchPages.length} className="p-0.5 rounded hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-30 text-gray-500 transition-colors"><ChevronDown size={14} /></button>
          <button onClick={() => { setSearchOpen(false); setSearchQuery(''); }} className="p-0.5 rounded hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-400 hover:text-gray-600 transition-colors ml-1"><X size={14} /></button>
        </div>
      )}

      {/* TOC Sidebar */}
      {outline.length > 0 && (
        <div className={`absolute left-0 top-0 bottom-0 z-20 flex transition-[width] duration-300 overflow-hidden ${tocOpen ? 'w-64' : 'w-0'}`}>
          <div className="w-64 flex-shrink-0 h-full bg-white dark:bg-slate-800 border-r border-gray-200 dark:border-slate-700 shadow-xl flex flex-col">
            <div className="flex items-center justify-between px-3 py-2.5 border-b border-gray-100 dark:border-slate-700 flex-shrink-0">
              <span className="text-sm font-bold text-gray-800 dark:text-white">
                {language === 'vietnamese' ? 'Mục lục' : 'Contents'}
              </span>
              <button onClick={() => setTocOpen(false)} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors">
                <X size={14} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto py-1">
              {renderTocItems(outline)}
            </div>
          </div>
        </div>
      )}

      <div
        id="pdf-viewer-container"
        ref={containerRef}
        onScroll={handleScroll}
        onWheel={handleWheel}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleContainerMouseDown}
        className={`flex-1 overflow-auto p-4 md:p-12 ${activeTool === 'move' ? 'cursor-grab active:cursor-grabbing' : 'cursor-default'}`}
        style={{
            touchAction: (activeTool === 'move' || activeTool === 'text_highlight') ? 'pan-x pan-y' : 'none',
            overscrollBehavior: 'contain',
            WebkitOverflowScrolling: 'touch',
        }}
      >
         <div
            ref={innerContainerRef}
            className="flex flex-col items-center min-h-full pb-32 min-w-full origin-top-left"
            style={pdfDarkMode ? { filter: 'invert(1) hue-rotate(180deg)' } : undefined}
         >
            {pages.map(pageNum => {
                const isInRange = pageNum >= visibleRange.start && pageNum <= visibleRange.end;
                // Placeholder dimensions — must be integer pixels to match PdfPage's rounded displayHeight
                const phWidth = Math.round(pageNaturalDims ? pageNaturalDims.width * scale : scale * 600);
                const phHeight = Math.round(pageNaturalDims ? pageNaturalDims.height * scale : scale * 840);
                return (
                <div key={pageNum} className="relative flex justify-center mb-4"
                     style={!isInRange ? { width: phWidth, height: phHeight } : undefined}>
                  {isInRange ? (
                    <>
                      <PdfPage
                          pdfDoc={pdfDoc}
                          pageNumber={pageNum}
                          scale={scale}
                          setPageRef={handleSetPageRef}
                          setHighlightLayerRef={handleSetHighlightLayerRef}
                          getHighlightLayerCanvas={getHighlightLayerCanvas}
                          scrollContainerRef={containerRef}
                          activeTool={activeTool}
                          registerAnnotationRef={registerAnnotationRef}
                          onSnap={onSnap}
                          onCropComplete={() => setActiveTool('move')}
                          initialStrokes={annotations[pageNum]}
                          onStrokesChange={(strokes) => handleStrokesChange(pageNum, strokes)}
                          currentPenWidth={penWidth}
                          currentHighlightWidth={highlightWidth}
                          currentColor={activeColor}
                          customColors={customColors}
                          onSaveHighlightColor={saveHighlightColor}
                          language={language}
                          guidedReadingHighlights={pageNum === currentPage ? guidedReadingHighlights : []}
                          setTextLayerRef={handleSetTextLayerRef}
                      />
                    </>
                  ) : (
                    /* Lightweight placeholder — keeps scroll height correct without rendering canvases */
                    <div className="bg-white dark:bg-slate-800 shadow-sm flex-shrink-0"
                         style={{ width: phWidth, height: phHeight }} />
                  )}
                </div>
                );
            })}
         </div>
      </div>

      <div className="absolute bottom-24 right-4 bg-gray-900/75 backdrop-blur-md text-white px-3 py-1.5 rounded-full text-xs font-semibold shadow-lg pointer-events-none z-10 tabular-nums">
          {currentPage} / {numPages}
      </div>

      {/* ── Text Selection Popup ── */}
      {selectionPopup && ReactDOM.createPortal(
        <div
          id="pdf-selection-popup"
          className="fixed z-[9999] bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-slate-700 p-3 flex flex-col gap-2.5 animate-in fade-in zoom-in-95 duration-100"
          style={{ left: selectionPopup.x, top: selectionPopup.y - 8, transform: 'translate(-50%, -100%)', minWidth: 230 }}
          onMouseDown={e => e.preventDefault()}
        >
          {/* Color swatches row */}
          <div className="flex items-center gap-1.5 flex-wrap">
            {[...HIGHLIGHT_COLORS.slice(0, 6), ...customColors].map(c => (
              <button
                key={c}
                className={`w-5 h-5 rounded-full border-2 transition-all hover:scale-125 ${highlightColor === c ? 'border-blue-500 scale-125' : 'border-gray-200 dark:border-slate-600'}`}
                style={{ backgroundColor: c }}
                onMouseDown={e => { e.preventDefault(); saveHighlightColor(c); setShowPickerInPopup(false); }}
              />
            ))}
            <button
              className={`w-5 h-5 rounded-full border-2 border-dashed flex items-center justify-center text-[10px] font-bold transition-colors ${showPickerInPopup ? 'border-blue-400 text-blue-500 bg-blue-50' : 'border-gray-300 text-gray-400 hover:border-gray-500'}`}
              onMouseDown={e => { e.preventDefault(); setShowPickerInPopup(v => !v); }}
              title={language === 'vietnamese' ? 'Màu tùy chỉnh' : 'Custom color'}
            >+</button>
          </div>
          {/* Expanded color grid — inline */}
          {showPickerInPopup && <ColorPickerGrid onSelect={c => { saveHighlightColor(c); }} onClose={() => setShowPickerInPopup(false)} />}
          {/* Opacity slider */}
          <div className="flex items-center gap-2">
            <Layers size={13} className="text-gray-400 shrink-0" />
            <input
              type="range" min="0.1" max="1.0" step="0.05" value={highlightAlpha}
              onMouseDown={e => { e.stopPropagation(); window.getSelection()?.removeAllRanges(); }}
              onChange={e => saveHighlightAlpha(parseFloat(e.target.value))}
              className="flex-1 h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
            <span className="text-xs w-8 text-right font-mono text-gray-500">{Math.round(highlightAlpha * 100)}%</span>
          </div>
          {/* Divider */}
          <div className="h-px bg-gray-100 dark:bg-slate-700" />
          {/* Action buttons */}
          <div className="flex items-center gap-1">
            <button
              className="flex-1 flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-xl text-xs font-medium bg-gray-50 dark:bg-slate-700 hover:bg-gray-100 dark:hover:bg-slate-600 transition-colors text-gray-700 dark:text-gray-200"
              onMouseDown={e => { e.preventDefault(); triggerTextHighlight(highlightColor, highlightAlpha); setSelectionPopup(null); }}
            >
              <Highlighter size={12} style={{ color: highlightColor }} />
              {language === 'vietnamese' ? 'Bôi màu' : 'Highlight'}
            </button>
            <button
              className="flex-1 flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-xl text-xs font-medium bg-gray-50 dark:bg-slate-700 hover:bg-gray-100 dark:hover:bg-slate-600 transition-colors text-gray-500 dark:text-gray-400"
              onMouseDown={e => { e.preventDefault(); const sel = window.getSelection(); if (sel) navigator.clipboard.writeText(sel.toString()).catch(() => {}); setSelectionPopup(null); }}
            >
              <Type size={12} />
              {language === 'vietnamese' ? 'Sao chép' : 'Copy'}
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* ANNOTATION TOOLBAR — renders in tab header via portal when available, else bottom float */}
      {(() => {
        const isDrawingTool = activeTool === 'pen' || activeTool === 'highlight' || activeTool === 'text_highlight' || activeTool === 'rect' || activeTool === 'arrow';
        const sep = <div className="w-px h-5 bg-gray-200 dark:bg-slate-600 flex-shrink-0" />;

        const toolbarJSX = toolbarPortalTarget ? (
          /* ── COMPACT HEADER TOOLBAR ── */
          <div className="flex items-center gap-0.5">
            <button onClick={() => { setSearchOpen(v => !v); setTimeout(() => searchInputRef.current?.focus(), 50); }} className={`p-1.5 rounded-lg transition-all flex-shrink-0 ${searchOpen ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-700'}`} title={language === 'vietnamese' ? 'Tìm kiếm (Cmd+F)' : 'Search (Cmd+F)'}><Search size={15} /></button>
            {sep}
            <button onClick={() => selectTool('move')} className={`p-1.5 rounded-lg transition-all flex-shrink-0 ${activeTool === 'move' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-700'}`} title="Pan"><Hand size={15} /></button>
            {sep}
            <button onClick={() => selectTool('highlight')} className={`p-1.5 rounded-lg transition-all flex-shrink-0 ${activeTool === 'highlight' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-700'}`} title="Highlight"><Highlighter size={15} /></button>
            <button onClick={handleHighlightToolClick} className={`p-1.5 rounded-lg transition-all flex-shrink-0 ${activeTool === 'text_highlight' ? 'bg-green-100 dark:bg-green-900/30 text-green-600' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-700'}`} title="Text Highlight"><Type size={15} /></button>
            <button onClick={() => selectTool('pen')} className={`p-1.5 rounded-lg transition-all flex-shrink-0 ${activeTool === 'pen' ? 'bg-red-100 dark:bg-red-900/30 text-red-600' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-700'}`} title="Pen"><PenTool size={15} /></button>
            <button onClick={() => selectTool('eraser')} className={`p-1.5 rounded-lg transition-all flex-shrink-0 ${activeTool === 'eraser' ? 'bg-gray-200 dark:bg-slate-600 text-gray-800' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-700'}`} title="Eraser"><Eraser size={15} /></button>
            <button onClick={() => selectTool('rect')} className={`p-1.5 rounded-lg transition-all flex-shrink-0 ${activeTool === 'rect' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-700'}`} title="Rectangle"><Square size={15} /></button>
            <button onClick={() => selectTool('arrow')} className={`p-1.5 rounded-lg transition-all flex-shrink-0 ${activeTool === 'arrow' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-700'}`} title="Arrow"><ArrowUpRight size={15} /></button>
            {sep}
            <div className="relative flex-shrink-0">
              <button id="pdf-capture-btn" onClick={() => selectTool('crop')} className={`p-1.5 rounded-lg transition-all ${activeTool === 'crop' ? 'bg-indigo-600 text-white' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-700'} ${highlightCaptureButton && activeTool !== 'crop' ? 'ring-2 ring-indigo-400 animate-pulse bg-indigo-50 text-indigo-600' : ''}`} title="Capture">
                <Camera size={15} />
              </button>
              {highlightCaptureButton && activeTool !== 'crop' && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-48 bg-gray-900 text-white rounded-xl p-2.5 shadow-xl pointer-events-none z-50 text-[11px]">
                  {language === 'vietnamese' ? 'Chụp công thức/đoạn văn từ PDF vào note.' : 'Capture formulas or paragraphs into a note.'}
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 border-4 border-transparent border-b-gray-900" />
                </div>
              )}
            </div>
            {sep}
            {onGuidedReading && (
              <button
                onClick={async () => { if (!pdfDoc || isGuidedReadingLoading) return; const text = await getPageText(currentPage); onGuidedReading(text, currentPage); }}
                disabled={!pdfDoc || isGuidedReadingLoading}
                className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium transition-all flex-shrink-0 ${isGuidedReadingLoading ? 'bg-amber-100 text-amber-500' : 'text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20'}`}
              >
                {isGuidedReadingLoading ? <Loader2 size={12} className="animate-spin" /> : <Lightbulb size={12} />}
                <span>{language === 'vietnamese' ? 'Gợi ý đọc' : 'Guided'}</span>
              </button>
            )}
            <button onClick={() => setVocabPanelOpen(v => !v)} disabled={!pdfDoc} className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium transition-all flex-shrink-0 ${vocabPanelOpen ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-700'}`}>
              <BookOpen size={12} />
              <span>{language === 'vietnamese' ? 'Từ khó' : 'Vocab'}</span>
            </button>
            {sep}
            <button
              onClick={() => { const next = !pdfDarkMode; setPdfDarkMode(next); localStorage.setItem('pdf_dark_mode', String(next)); }}
              className={`p-1.5 rounded-lg transition-all flex-shrink-0 ${pdfDarkMode ? 'bg-slate-700 text-amber-400' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-700'}`}
              title={language === 'vietnamese' ? (pdfDarkMode ? 'Tắt dark mode PDF' : 'Bật dark mode PDF') : (pdfDarkMode ? 'PDF Light mode' : 'PDF Dark mode')}
            >
              {pdfDarkMode ? <Sun size={15} /> : <Moon size={15} />}
            </button>
            {/* Inline color/size when drawing tool active */}
            {isDrawingTool && (
              <>
                {sep}
                {activePalette.map(c => (
                  <button key={c} onClick={() => { if (activeTool === 'highlight' || activeTool === 'text_highlight') setHighlightColor(c); else setPenColor(c); }} className={`w-5 h-5 rounded-full border border-gray-200 flex-shrink-0 transition-transform ${activeColor === c ? 'ring-2 ring-blue-500 scale-110' : ''}`} style={{ backgroundColor: c }} />
                ))}
                <div className="relative w-5 h-5 flex-shrink-0">
                  <button onClick={() => colorInputRef.current?.click()} className="w-5 h-5 rounded-full border border-gray-300 bg-white flex items-center justify-center hover:bg-gray-100"><Plus size={11} className="text-gray-600" /></button>
                  <input ref={colorInputRef} type="color" className="absolute inset-0 opacity-0 w-full h-full" onChange={handleColorPick} value={activeColor} />
                </div>
                {activeTool !== 'text_highlight' && (
                  <input type="range" min={activeTool === 'highlight' ? "10" : "1"} max={activeTool === 'highlight' ? "50" : "20"} step="1" value={activeTool === 'highlight' ? highlightWidth : penWidth} onChange={(e) => activeTool === 'highlight' ? setHighlightWidth(parseInt(e.target.value)) : setPenWidth(parseInt(e.target.value))} className="w-16 h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer flex-shrink-0" />
                )}
              </>
            )}
          </div>
        ) : (
          /* ── ORIGINAL BOTTOM FLOATING TOOLBAR (fallback / mobile) ── */
          <div className={`fixed md:absolute bottom-6 left-1/2 -translate-x-1/2 z-30 transition-all duration-300 ${toolbarOpen ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0 pointer-events-none'}`}>
            <div className="bg-white/95 dark:bg-slate-800/95 backdrop-blur-md border border-gray-200 dark:border-slate-700 shadow-2xl rounded-2xl p-2 flex flex-col gap-2 max-w-[95vw]">
              <div className="flex items-center gap-2 overflow-x-auto px-1 hide-scrollbar">
                <button onClick={() => selectTool('move')} className={`p-2 md:p-3 rounded-xl transition-all flex-shrink-0 ${activeTool === 'move' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-700'}`}><Hand className="w-5 h-5 md:w-6 md:h-6" /></button>
                <div className="w-px h-6 md:h-8 bg-gray-200 dark:bg-slate-700 flex-shrink-0" />
                <button onClick={() => selectTool('highlight')} className={`p-2 md:p-3 rounded-xl transition-all flex-shrink-0 ${activeTool === 'highlight' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 ring-2 ring-yellow-400/50' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-700'}`}><Highlighter className="w-5 h-5 md:w-6 md:h-6" /></button>
                <button onClick={handleHighlightToolClick} className={`p-2 md:p-3 rounded-xl transition-all flex-shrink-0 ${activeTool === 'text_highlight' ? 'bg-green-100 dark:bg-green-900/30 text-green-600 ring-2 ring-green-400/50' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-700'}`}><Type className="w-5 h-5 md:w-6 md:h-6" /></button>
                <button onClick={() => selectTool('pen')} className={`p-2 md:p-3 rounded-xl transition-all flex-shrink-0 ${activeTool === 'pen' ? 'bg-red-100 dark:bg-red-900/30 text-red-600 ring-2 ring-red-400/50' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-700'}`}><PenTool className="w-5 h-5 md:w-6 md:h-6" /></button>
                <button onClick={() => selectTool('eraser')} className={`p-2 md:p-3 rounded-xl transition-all flex-shrink-0 ${activeTool === 'eraser' ? 'bg-gray-200 dark:bg-slate-600 text-gray-800' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-700'}`}><Eraser className="w-5 h-5 md:w-6 md:h-6" /></button>
                <button onClick={() => selectTool('rect')} className={`p-2 md:p-3 rounded-xl transition-all flex-shrink-0 ${activeTool === 'rect' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 ring-2 ring-blue-400/50' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-700'}`} title={language === 'vietnamese' ? 'Vẽ hình chữ nhật' : 'Rectangle'}><Square className="w-5 h-5 md:w-6 md:h-6" /></button>
                <button onClick={() => selectTool('arrow')} className={`p-2 md:p-3 rounded-xl transition-all flex-shrink-0 ${activeTool === 'arrow' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 ring-2 ring-blue-400/50' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-700'}`} title={language === 'vietnamese' ? 'Vẽ mũi tên' : 'Arrow'}><ArrowUpRight className="w-5 h-5 md:w-6 md:h-6" /></button>
                <div className="w-px h-6 md:h-8 bg-gray-200 dark:bg-slate-700 flex-shrink-0" />
                <div className="relative flex-shrink-0">
                  <button id="pdf-capture-btn" onClick={() => selectTool('crop')} className={`p-2 md:p-3 rounded-xl transition-all ${activeTool === 'crop' ? 'bg-indigo-600 text-white shadow-lg' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-700'} ${highlightCaptureButton && activeTool !== 'crop' ? 'ring-2 ring-indigo-400 ring-offset-1 animate-pulse bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400' : ''}`}><Camera className="w-5 h-5 md:w-6 md:h-6" /></button>
                  {highlightCaptureButton && activeTool !== 'crop' && (
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-52 bg-gray-900 dark:bg-slate-700 text-white rounded-xl p-3 shadow-xl pointer-events-none z-50">
                      <p className="text-xs font-semibold mb-1">{language === 'vietnamese' ? 'Capture đoạn quan trọng' : 'Capture important passages'}</p>
                      <p className="text-[11px] text-gray-300 leading-relaxed">{language === 'vietnamese' ? 'Dùng công cụ này để chụp công thức, đoạn văn hoặc hình ảnh từ PDF vào note.' : 'Use this tool to capture formulas, paragraphs, or images from the PDF into a note.'}</p>
                      <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900 dark:border-t-slate-700" />
                    </div>
                  )}
                </div>
                {isMobile && onVoiceCapture && (
                  <button onClick={() => setVoiceCaptureOpen(true)} disabled={!pdfDoc} className="p-2 rounded-xl transition-all flex-shrink-0 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 disabled:opacity-40" title={language === 'vietnamese' ? 'Ghi chú bằng giọng nói' : 'Voice Note'}><Mic className="w-5 h-5" /></button>
                )}
              </div>
              {onGuidedReading && (
                <div className="flex items-center gap-2 px-1 border-t border-gray-100 dark:border-slate-700 pt-1.5">
                  <button onClick={async () => { if (!pdfDoc || isGuidedReadingLoading) return; const text = await getPageText(currentPage); onGuidedReading(text, currentPage); }} disabled={!pdfDoc || isGuidedReadingLoading} className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-xl transition-all text-xs font-medium ${isGuidedReadingLoading ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-500' : 'text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20'}`}>
                    {isGuidedReadingLoading ? <Loader2 size={14} className="animate-spin" /> : <Lightbulb size={14} />}
                    <span>{language === 'vietnamese' ? 'Gợi ý đọc' : 'Guided'}</span>
                  </button>
                  <button onClick={() => setVocabPanelOpen(v => !v)} disabled={!pdfDoc} className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-xl transition-all text-xs font-medium ${vocabPanelOpen ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-700'}`}>
                    <BookOpen size={14} />
                    <span>{language === 'vietnamese' ? 'Từ khó' : 'Vocab'}</span>
                  </button>
                  <button
                    onClick={() => { const next = !pdfDarkMode; setPdfDarkMode(next); localStorage.setItem('pdf_dark_mode', String(next)); }}
                    className={`flex-shrink-0 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-xl transition-all text-xs font-medium ${pdfDarkMode ? 'bg-slate-700 text-amber-400' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-700'}`}
                    title={language === 'vietnamese' ? (pdfDarkMode ? 'Tắt dark mode PDF' : 'Bật dark mode PDF') : (pdfDarkMode ? 'PDF Light mode' : 'PDF Dark mode')}
                  >
                    {pdfDarkMode ? <Sun size={14} /> : <Moon size={14} />}
                  </button>
                </div>
              )}
              {isDrawingTool && (
                <div className="flex items-center gap-3 px-2 py-1 bg-gray-50 dark:bg-slate-900/50 rounded-xl overflow-x-auto hide-scrollbar animate-in slide-in-from-bottom-2">
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {activePalette.map(c => (
                      <button key={c} onClick={() => { if (activeTool === 'highlight' || activeTool === 'text_highlight') setHighlightColor(c); else setPenColor(c); }} className={`w-6 h-6 rounded-full border border-gray-200 transition-transform ${activeColor === c ? 'ring-2 ring-blue-500 scale-110' : ''}`} style={{ backgroundColor: c }} />
                    ))}
                    <div className="relative w-6 h-6 flex-shrink-0 ml-1">
                      <button onClick={() => colorInputRef.current?.click()} className="w-6 h-6 rounded-full border border-gray-300 bg-white flex items-center justify-center hover:bg-gray-100"><Plus size={14} className="text-gray-600" /></button>
                      <input ref={colorInputRef} type="color" className="absolute inset-0 opacity-0 w-full h-full" onChange={handleColorPick} value={activeColor} />
                    </div>
                  </div>
                  {activeTool !== 'text_highlight' && (
                    <>
                      <div className="w-px h-6 bg-gray-300 dark:bg-slate-600 mx-1 flex-shrink-0" />
                      <input type="range" min={activeTool === 'highlight' ? "10" : "1"} max={activeTool === 'highlight' ? "50" : "20"} step="1" value={activeTool === 'highlight' ? highlightWidth : penWidth} onChange={(e) => activeTool === 'highlight' ? setHighlightWidth(parseInt(e.target.value)) : setPenWidth(parseInt(e.target.value))} className="w-20 h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer flex-shrink-0" />
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        );

        return toolbarPortalTarget
          ? ReactDOM.createPortal(toolbarJSX, toolbarPortalTarget)
          : toolbarJSX;
      })()}
      
      {/* Zoom / Toggle Toolbar Controls */}
      <div className="absolute bottom-24 flex flex-col gap-2 z-20 transition-all duration-300" style={{ left: tocOpen ? '272px' : '16px' }}>
          {outline.length > 0 && (
            <button
              onClick={() => setTocOpen(o => !o)}
              className={`p-3 rounded-full shadow-lg border transition-colors ${tocOpen ? 'bg-blue-600 text-white border-blue-600' : 'bg-white dark:bg-slate-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-slate-700 hover:text-blue-600'}`}
              title={language === 'vietnamese' ? 'Mục lục' : 'Table of Contents'}
            >
              <List size={20} />
            </button>
          )}
          {!toolbarPortalTarget && (
            <button onClick={() => setToolbarOpen(!toolbarOpen)} className="p-3 bg-white dark:bg-slate-800 rounded-full shadow-lg border border-gray-200 dark:border-slate-700 text-gray-600 dark:text-gray-300">
               {toolbarOpen ? <ChevronDown size={20} /> : <ChevronUp size={20} />}
            </button>
          )}
          <button onClick={() => zoomAtViewportCenter(0.2)} className="p-3 bg-white dark:bg-slate-800 rounded-full shadow-lg border border-gray-200 dark:border-slate-700 text-gray-600 dark:text-gray-300">
             <ZoomIn size={20} />
          </button>
          <button onClick={() => zoomAtViewportCenter(-0.2)} className="p-3 bg-white dark:bg-slate-800 rounded-full shadow-lg border border-gray-200 dark:border-slate-700 text-gray-600 dark:text-gray-300">
             <ZoomOut size={20} />
          </button>
      </div>

      {/* Page Vocabulary Panel */}
      <PageVocabularyPanel
        isOpen={vocabPanelOpen}
        onClose={() => setVocabPanelOpen(false)}
        getPageText={getPageText}
        pageNumber={currentPage}
        documentTitle={documentTitle}
        boardTitle={boardTitle}
        documentId={url}
        language={language}
      />

      {/* Mobile voice capture overlay */}
      {voiceCaptureOpen && onVoiceCapture && (
          <MobileVoiceCaptureOverlay
              language={language}
              pageCanvases={pageCanvases}
              parentNoteName={selectedNoteName}
              onComplete={(imageBase64, transcript) => {
                  setVoiceCaptureOpen(false);
                  onVoiceCapture(imageBase64, transcript);
              }}
              onCancel={() => setVoiceCaptureOpen(false)}
          />
      )}

      {/* Admin debug overlay — only visible to han.vas2018@gmail.com */}
      {userEmail === 'han.vas2018@gmail.com' && (() => {
        let saved: { page?: number; scrollTop?: number; scale?: number } = {};
        try { saved = JSON.parse(localStorage.getItem(pdfStateKey) || '{}'); } catch {}
        const toggleDebug = (hidden: boolean) => {
          localStorage.setItem('pdf_debug_hidden', String(hidden));
          setDebugHidden(hidden);
        };
        return debugHidden ? (
          <button
            className="hidden md:flex absolute bottom-16 right-3 z-50 bg-black/60 text-yellow-300 text-[10px] font-mono rounded-lg px-2 py-1 items-center gap-1 hover:bg-black/80 transition-colors"
            onClick={() => toggleDebug(false)}
            title="Show PDF Debug"
          >
            📍
          </button>
        ) : (
          <div className="hidden md:block absolute bottom-16 right-3 z-50 bg-black/75 text-white text-[10px] font-mono rounded-lg px-3 py-2 space-y-0.5 select-none">
            <div className="flex items-center justify-between mb-1">
              <span className="text-yellow-300 font-bold">📍 PDF Debug</span>
              <button
                className="ml-3 text-white/50 hover:text-white transition-colors leading-none px-1"
                onClick={() => toggleDebug(true)}
                title="Hide"
              >✕</button>
            </div>
            <div>saved page: <span className="text-green-300">{saved.page ?? '—'}</span></div>
            <div>saved scroll: <span className="text-green-300">{saved.scrollTop != null ? Math.round(saved.scrollTop) : '—'}px</span></div>
            <div>saved zoom: <span className="text-green-300">{saved.scale != null ? (saved.scale * 100).toFixed(0) + '%' : '—'}</span></div>
            <div className="border-t border-white/20 mt-1 pt-1">
              <div>current page: <span className="text-cyan-300">{currentPage}</span></div>
              <div>current zoom: <span className="text-cyan-300">{(scale * 100).toFixed(0)}%</span></div>
              <div>total pages: <span className="text-cyan-300">{numPages}</span></div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};
